import { fmtDistance } from './units'
// Igual que la app del oficial: el idioma base es inglés y el español es una traducción. Ningún
// texto visible se escribe dentro de un componente.

export type Lang = 'en' | 'es'

const en = {
  'app.title': 'PROTECT Command Center',
  'app.signIn': 'Sign in',
  'app.server': 'Server',
  'app.agency': 'Agency',
  'app.signOut': 'Sign out',
  'app.signInProvider': 'Sign in with the agency provider',
  'app.noProvider': 'This deployment has no identity provider configured yet.',
  'app.signedInAs': 'Signed in as {0}',
  'app.theme': 'Switch theme',
  'app.language': 'Switch language',
  'app.refresh': 'Refresh',

  // Los cuatro estados de un listado (§13). Deliberadamente genéricos: los usa cualquier panel,
  // y el mensaje concreto de «no hay ninguno» lo pone cada uno con su propia clave.
  // Importar plantilla y flota (§8.1). Los códigos de rechazo se traducen aquí porque el
  // servidor manda el código y nunca la frase: no sabe en qué idioma está mirando quien recibe.
  // El reporte del periodo (§16.3). Va a una junta de cabildo o a un mando que no abre el
  // sistema: lleva de qué agencia es, de qué periodo, quién lo sacó y con qué salvedades se lee.
  // Los turnos de la agencia (§16.3). Venían fijos en el producto: tres de ocho horas.
  // Los límites de los distritos (§16.3).
  'districts.title': 'District boundaries',
  'districts.help': 'Upload the boundaries your city publishes, in GeoJSON. With them, which division a hotspot belongs to comes from where it is, instead of from the beat recorded on its incidents. Uploading again replaces the whole set.',
  'districts.file': 'GeoJSON file',
  'districts.upload': 'Upload boundaries',
  'districts.uploading': 'Uploading…',
  'districts.loaded': '{0} of {1} boundaries loaded.',
  'districts.none': 'No boundaries loaded. Divisions are resolved by the beat prefix.',
  'geo.validFrom': 'In force from (leave empty for now)',
  'geo.validFromHelp': 'Nothing is deleted: the version in force is closed and this one starts on that date. A redistricting can be loaded ahead of time. It cannot start in the past.',
  'geo.version': 'Version {0}, in force from {1}.',
  'geo.versionScheduled': 'Version {0} is scheduled: it starts {1}. Until then version {2} stays in force.',
  'geo.versionScheduledFirst': 'Version {0} is scheduled: it starts {1}. Until then there is none in force.',
  'geo.colVersion': 'Version', 'geo.colFrom': 'From', 'geo.colTo': 'Until', 'geo.colAreas': 'Areas', 'geo.colBy': 'Loaded by',
  'geo.open': 'in force', 'geo.scheduled': 'scheduled', 'geo.always': 'always',
  'geo.overridden': 'Heads up: the load scheduled for {0} will replace this one on that date.',
  'districts.rejected': '{0} were not loaded:',
  'districts.problem.missing_code': 'no code',
  'districts.problem.not_an_area': 'not a polygon',
  'districts.problem.duplicate': 'code already in the file',
  'districts.entry': 'entry {0}',
  'denial.reference': 'ref. {0}',
  'zones.title': 'Patrol zones',
  'zones.help': 'Upload the shapes your agency patrols, in GeoJSON: a park, a shopping strip, a stretch of avenue. With them, "out of zone" is measured against the shape instead of a radius around the hotspot. Uploading again replaces the whole set.',
  'zones.file': 'GeoJSON file with the zones',
  'zones.upload': 'Upload zones',
  'zones.uploading': 'Uploading…',
  'zones.loaded': '{0} of {1} zones loaded.',
  'zones.none': 'No zones loaded. Out of zone is measured as a radius around the hotspot.',
  'zones.rejected': '{0} were not loaded:',
  'zones.entry': 'entry {0}',

  'shifts.title': 'Shifts of this agency',
  'shifts.help': 'What time each shift starts and ends, always in agency time ({0}), even when the rest of the screen is in your own time: it defines the agency schedule. Saving replaces the whole list: whatever is not here stops existing. Shifts already recorded keep working even if you remove them here.',
  'shifts.code': 'Code',
  'shifts.name': 'Name',
  'shifts.start': 'Starts',
  'shifts.end': 'Ends',
  'shifts.hours': 'Length',
  'shifts.duration': '{0} h',
  'shifts.overnight': 'ends next day',
  'shifts.add': 'Add shift',
  'shifts.remove': 'Remove',
  'shifts.save': 'Save shifts',
  'shifts.saved': 'Saved: this is the agency schedule now',
  'shifts.duplicate': 'Two shifts share a code. The code travels on every plan and every unit, so it has to be unique.',
  'shifts.incomplete': 'Every shift needs a code, a name, and two different times.',

  'report.open': 'Report for printing',
  'report.print': 'Print',
  'report.close': 'Back',
  'report.title': 'Shift performance report',
  'report.issued': 'Issued {0} by {1}.',
  'report.partials': '{0} of the {1} shifts in this period are partial: they were approved knowing something was left uncovered. Their numbers are in the totals and pull them down.',
  'report.planned': 'Assignments planned',
  'report.released': 'Released to a unit',
  'report.completed': 'Completed',
  'report.deferred': 'Left deferred',
  'report.incidents': 'Incidents',
  'report.division': 'Division',
  'report.footer': 'PROTECT — generated from the shift records of this agency.',

  'import.officers': 'Import roster',
  'import.vehicles': 'Import fleet',
  'import.help': 'Upload the file your personnel or fleet system exports, as .csv or .xlsx. Uploading it again updates what is already here instead of duplicating it.',
  'import.file': 'File to upload',
  'import.upload': 'Upload',
  'import.uploading': 'Uploading…',
  'import.written': '{0} of {1} rows loaded.',
  'import.absent': '{0} of the {1} already registered came in this file. The other {2} did NOT come, and were NOT deactivated. Check whether the export was complete.',
  'import.rejected': '{0} {0|row was|rows were} not loaded. The rest did load.',
  'import.clean': 'Every row loaded.',
  'import.kept': 'The file has no column for {0}: whoever was already registered keeps what they had.',
  'import.field.certs': 'certifications', 'import.field.hr': 'HR reference', 'import.field.equipment': 'equipment', 'import.field.active': 'status',
  'import.ignoredPersonal': 'Personal data columns not stored: {0}. PROTECT does not keep personal data.',
  'import.ignoredUnknown': 'Columns not recognized and not used: {0}. If one carries something PROTECT needs (badge, rank, division, start date, certifications), rename its header and upload again.',
  'import.line': 'Line',
  'import.column': 'Column',
  'import.problem': 'Problem',
  'import.more': 'And {0} more.',
  'import.fixAndRetry': 'Fix those lines and upload the file again: it updates instead of duplicating.',
  'import.problem.missing': 'A required value is missing.',
  'import.problem.bad_date': 'The date could not be read. Use YYYY-MM-DD.',
  'import.problem.bad_value': 'That value is not one of the accepted ones.',
  'import.problem.bad_status': 'Status not recognized, so the row was not loaded: a vehicle that may be in the shop is not sent out as available. Use available, maintenance or out_of_service (disponible, taller, baja).',
  'import.problem.duplicate': 'That number already appeared earlier in the file.',
  'import.problem.too_many_rows': 'The file has too many rows. Nothing was loaded.',
  'import.problem.unreadable': 'The file could not be read. Check that it is a real .csv or .xlsx.',

  'state.loading': 'Loading…',
  'state.error': 'Could not be loaded. The message above says why.',
  'state.stale': 'Showing the last data received: the latest refresh failed.',

  'command.proposalWaiting': '{1} proposed version {0} of the plan for the shift under way: it replaces the current one once you approve it.',
  'command.reviewProposal': 'Review it in Planning',
  'app.helpNoPlan': 'Shift {0} is running without a published plan: there is nothing to watch against until the watch commander approves one. The top of the screen says what is missing and whose turn it is.',
  'app.help': 'The plan is running. Here you watch it against what units actually do: act on each deviation, follow incidents, and ask for a reoptimization when the shift changes.',
  'alerts.help': 'Where the plan and the street disagree. Pick one to see it on the map.',
  'incidents.help': 'What units reported from the field.',
  'replan.help': 'Asks TACTIX to replan the rest of the shift for the affected divisions.',
  'units.help': 'Who is on duty, where they are and whether the phone is reporting.',
  'map.noAlertPosition': 'This deviation has no place on the map yet: the unit has not reported a position.',
  'map.broken': 'The map could not draw its data layers',

  'demo.title': 'Demonstration',
  'demo.help': 'Puts units to work against this same API, as if officers were carrying their phones. Only in a demo deployment.',
  'demo.units': 'Units',
  'demo.pace': 'Seconds per leg',
  'demo.deviate': 'One in how many misbehaves',
  'demo.deviateNone': 'None',
  'demo.start': 'Start',
  'demo.stop': 'Stop',
  'demo.running': 'Running · {0} {0|unit|units} · {1} {1|visit|visits} completed',
  'demo.deviating': 'Misbehaving on purpose: {0}',
  'demo.idle': 'Not running.',

  'units.title': 'Units',
  'units.none': 'No units on duty.',
  'units.filter': 'Filter by call sign or division',
  'units.lastSeen': 'Last seen {0}',
  'units.noPosition': 'No position yet',
  'units.assignment': 'On assignment',
  'units.next': 'Heading to',
  'units.interrupted': 'Visit on hold',
  'units.reason': 'Reason',
  'reassign.open': 'Reassign to another unit',
  'reassign.to': 'To',
  'reassign.why': 'Justification (required): it stays in the plan history',
  'reassign.help': 'Publishes a new plan version signed by you. The officer who had it is told it was withdrawn; the other unit receives it.',
  'reassign.confirm': 'Reassign',
  'reassign.cancel': 'Cancel',
  'reassign.done': '{0} was reassigned to {1} (plan version {2}).',
  'reassign.sent': '{0} now goes to {1}: their phone gets it in a few seconds.',
  'reassign.working': 'Reassigning…',
  'cancelVisit.open': 'Cancel this visit',
  'cancelVisit.why': 'Reason (required): it stays in the plan history',
  'cancelVisit.help': 'Publishes a new plan version signed by you. Nobody gets the visit; the officer who had it is told it was withdrawn.',
  'cancelVisit.confirm': 'Cancel the visit',
  'cancelVisit.working': 'Cancelling…',
  'cancelVisit.done': '{0} was withdrawn from {1}: their phone is told in a few seconds.',

  'alerts.title': 'Plan deviations',
  'alerts.none': 'No open deviations.',
  'alerts.showAll': 'Show resolved too',
  'alerts.search': 'Search by unit, type or place',
  'alerts.sort': 'Order',
  'alerts.sort.severity': 'Most serious first',
  'alerts.sort.newest': 'Newest first',
  'alerts.sort.oldest': 'Oldest first',
  'alerts.sort.unit': 'By unit',
  'alerts.onlyVisible': 'Only what the map shows',
  'alerts.visit': 'Visit {0} · planned {1}',
  'alerts.samePlace': '{0} deviations at this same point',
  'alerts.samePlaceOnly': 'Show only these',
  'alerts.nearby': '{0} within {1}',
  'alerts.nearbyOnly': 'Show only these',
  'alerts.nearbyClear': 'Show all again',
  'alerts.showing': 'Showing {0} of {1}',
  'alerts.noneMatch': 'Nothing matches what you are looking for.',
  'units.noneMatch': 'No unit matches that filter.',
  'log.noneMatch': 'Nothing in the log matches these filters.',
  'alerts.assignTo': 'Assign to',
  'alerts.supportNote': 'How it was handled (required to resolve)',
  'alerts.replan': 'Reoptimize the plan',
  'alerts.note': 'Note',
  'alerts.action.Acknowledge': 'Acknowledge',
  'alerts.action.Assign': 'Assign',
  'alerts.action.Escalate': 'Escalate',
  'alerts.action.Resolve': 'Resolve',
  'alerts.action.Close': 'Close',
  'alerts.assignedTo': 'Assigned to {0}',

  'incidents.replanOut': '{0} is out of the plan while handling this. Ask for a reoptimization to spread its work.',
  'incidents.replanReturn': '{0} is back on duty. Ask for a reoptimization to include it again.',
  'incidents.replanAsk': 'Request reoptimization',
  'signin.authorized': 'Authorized personnel only',
  'signin.help': 'Sign in with your department account. The password and the second factor are asked by the identity provider of your agency; PROTECT never sees them.',
  'signin.sso': 'Sign in with SSO',
  'map.legendIncident': 'Incident',
  'incidents.showOnMap': 'Show on the map',
  'incidents.noLocation': 'No location: the phone had no position when it was reported.',
  'time.agency': 'Agency time · {0}',
  'time.mine': 'My time · {0}',
  'time.toMine': 'Times are shown in agency time. Click to see them in your own time.',
  'time.toAgency': 'Times are shown in your own time. Click to go back to agency time.',
  'signin.checking': 'Checking how to sign in here…',
  'signin.unreachable': 'The PROTECT server is not answering. Trying again by itself; if it goes on, check the network.',
  'signin.secureAccess': 'Secure operational access',
  'signin.https': 'Secure connection (HTTPS)',
  'signin.encrypted': 'Data encrypted at rest',
  'signin.theme': 'Switch theme',
  'signin.pillars': 'Intelligence  |  Operations  |  Safer communities',
  'signin.tagline': 'Predictive Readiness, Operations, Tactics & Enforcement Command Technology',
  'signin.dataToday': 'Data today.\nA safer tomorrow.',
  'signin.verbs': 'Analyze\nAnticipate\nDeploy\nProtect',
  'signin.copyright': '© {0} Intelligence for Defense & Security Inc. All rights reserved.',
  'signin.license': 'PROTECT™ is proprietary technology. Use is subject to the applicable license agreement.',
  'theme.title': 'Agency theme',
  'theme.help': 'How PROTECT presents itself to your people: on the web and phone sign-in, on the password page and throughout the application (in dark mode, background, panels, buttons and titles; in light mode, buttons and titles). Status colors (ok, warning, danger) do not change. The design belongs to PROTECT; the name, motto, colors and badge belong to your agency.',
  'theme.name': 'Agency name',
  'theme.nameHelp': 'As your people call it. Leave it empty to use the registered name.',
  'theme.motto': 'Motto (optional)',
  'theme.mottoExample': 'Service · Integrity · Community',
  'theme.demoNotice': 'Demo notice',
  'theme.demoNoticeExample': 'Demo: the data is not real',
  'theme.demoNoticeHelp': 'Only for demo agencies: shown on every screen, on the web and on the tablet. Leave it empty for a real agency.',
  'theme.presets': 'Themes',
  'theme.preset.protect': 'PROTECT',
  'theme.preset.navy': 'Navy',
  'theme.preset.sheriff': 'Forest and gold',
  'theme.preset.tan': 'Brown and tan',
  'theme.preset.graphite': 'Graphite',
  'theme.preset.burgundy': 'Burgundy',
  'theme.primary': 'Main color',
  'theme.primaryHelp': 'The background. It must be dark: the text on it is white.',
  'theme.accent': 'Accent color',
  'theme.accentHelp': 'Buttons, lines and the reticle. It must be light: the button text is dark.',
  'theme.problem.format': 'Colors go as #RRGGBB.',
  'theme.problem.primaryLight': 'The main color is too light: white text cannot be read on it. Choose a darker one.',
  'theme.problem.accentDark': 'The accent is too dark: the sign-in button text cannot be read on it. Choose a lighter one.',
  'theme.problem.accentBlends': 'The accent blends into the main color. Choose one that stands out more.',
  'theme.save': 'Save theme',
  'theme.discard': 'Discard changes',
  'theme.saved': 'Saved: this is what your people will see',
  'theme.badge': 'Badge',
  'theme.badgeHelp': 'Your agency badge or logo, PNG, JPEG or WebP up to 512 KB, ideally with a transparent background. SVG is not accepted: it can carry code, and the badge is shown before anyone signs in.',
  'theme.badgeChoose': 'Choose the badge image',
  'theme.badgeType': 'The badge must be a PNG, JPEG or WebP image.',
  'theme.badgeSize': 'The image weighs {0} KB; the maximum is 512 KB.',
  'theme.badgeUpload': 'Upload badge',
  'theme.badgeRemove': 'Remove badge',
  'theme.badgeNone': 'No badge loaded: a neutral shield with the agency initials is shown.',
  'theme.preview': 'Preview',
  'uncovered.title': 'Left without any visit ({0}), by risk:',
  'uncovered.item': '#{0} {1} · {2} · risk {3}',
  'uncovered.all': 'Show {0} more',
  'uncovered.fewer': 'Show fewer',
  'incidents.replanDone': 'Reoptimization requested',
  'incidents.reasonOut': '{0} is handling an incident: {1}',
  'incidents.reasonReturn': '{0} is back on duty after: {1}',
  'incidents.title': 'Incidents',
  'incidents.none': 'No incidents.',
  'incidents.action.Validate': 'Validate',
  'incidents.action.Activate': 'Activate',
  'incidents.action.Control': 'Under control',
  'incidents.action.Complete': 'Complete',
  'incidents.action.Cancel': 'Cancel',
  'incidents.cancelNote': 'Why it is cancelled (required)',
  'denial.incident.cancel_needs_note': 'To cancel the incident from {callSign}, note why.',

  'replan.title': 'Reoptimization',
  'replan.shiftDate': 'Shift date',
  'replan.shiftCode': 'Shift',
  'replan.reason': 'Reason',
  'replan.request': 'Request reoptimization',
  'replan.requested': 'Requested. The result arrives on this screen.',
  'replan.result.Proposed': '{0}: revision {1} proposed — {2} new {2|visit|visits}, {3} withdrawn. The watch commander decides it (above).',
  'replan.result.NoChanges': '{0}: no changes. The current plan is already the best with what is available.',
  'replan.result.Failed': '{0}: the reoptimization failed. The current plan stays in force; check the log.',

  'shift.running': 'On watch: {0} {1}',
  'shift.remaining': '{0} h {1} min left',
  'shift.alsoRunning': '{0} is also running.',
  'shift.alsoRunningNoPlan': '{0} is also running, with no published plan.',
  'shift.none': 'No shift is running right now, according to the agency shift templates.',

  'log.title': 'Shift log',
  'log.help': 'What has happened, most recent first. It runs across shifts: the handover is one line inside it, not a cut.',
  'log.window': 'How far back',
  'log.lastHours': 'Last {0} h',
  'log.filter': 'Show',
  'log.all': 'Everything',
  'log.unit': 'Unit',
  'log.showing': 'Showing {0} of {1}',
  'log.quiet': 'Nothing in this window. A quiet stretch is a quiet stretch.',
  'log.by': 'by {0}',
  'log.detected': 'detected by the system',
  'log.family.alert': 'Deviations',
  'log.family.incident': 'Incidents',
  'log.family.unit': 'Units',
  'log.family.reoptimization': 'Reoptimization',
  'log.family.handover': 'Handover',
  'log.family.plan': 'Plan',
  'log.alert.raised': 'Deviation raised: {kind} ({severity})',
  'log.alert.acknowledge': 'Deviation acknowledged',
  'log.alert.assign': 'Deviation assigned',
  'log.alert.escalate': 'Deviation escalated',
  'log.alert.resolve': 'Deviation resolved',
  'log.alert.autoresolve': 'Deviation cleared on its own',
  'log.alert.close': 'Deviation closed',
  'log.incident.reported': 'Incident reported from the field',
  'log.incident.validate': 'Incident validated',
  'log.incident.activate': 'Incident active',
  'log.incident.control': 'Incident under control',
  'log.incident.complete': 'Incident completed',
  'log.incident.cancel': 'Incident cancelled',
  'log.unit.StartShift': 'Started the shift',
  'log.unit.Acknowledge': 'Acknowledged an assignment',
  'log.unit.CheckIn': 'Checked in at the point',
  'log.unit.CheckOut': 'Left the point',
  'log.unit.IncidentStarted': 'Went to an incident',
  'log.unit.IncidentCompleted': 'Cleared the incident',
  'log.unit.EndShift': 'Ended the shift',
  'log.unit.rejected': 'The phone tried {kind} and was refused ({code})',
  'log.unit.datalost': 'The phone deleted {count} records it could not send: {reason}',
  'command.tabNow': 'Deviations',
  'command.tabNowBadge': 'New plan deviations',
  'command.tabIncidents': 'Incidents',
  'command.tabIncidentsBadge': 'Open incidents',
  'command.tabUnits': 'Units',
  'command.tabUnitsBadge': 'Units on duty without connection',
  'command.tabPlan': 'Plan',
  'command.tabPlanBadge': 'A revision is waiting for a decision',
  'command.tabShift': 'Shift',
  'resources.search': 'Search by badge, rank or division',
  'resources.searchVehicle': 'Search by unit number, type or division',
  'resources.filterStatus': 'Show',
  'resources.all': 'All · {0}',
  'resources.showing': 'Showing {0} of {1}',
  'resources.markShownAvailable': 'Mark the shown available ({0})',
  'resources.markShownOut': 'Take the shown off this shift ({0})',
  'resources.noneMatch': 'Nothing matches the search.',
  'admin.deadLetterReason': 'What was reviewed and what was done (required to discard)',
  'admin.deadLetterDiscard': 'Discard',
  'ready.title': 'Getting shift {0} ready ({1})',
  'ready.step.officers': 'HR publishes officers',
  'ready.step.vehicles': 'Fleet publishes vehicles',
  'ready.step.hotspots': 'Analyst publishes the shift hotspots',
  'ready.step.strategy': 'Chief approves the strategy',
  'ready.step.units': 'Scheduler forms units',
  'ready.step.proposal': 'Scheduler proposes the plan',
  'ready.step.approval': 'Watch commander approves and publishes',
  'ready.who.officers': 'HR', 'ready.who.vehicles': 'Fleet', 'ready.who.hotspots': 'the crime analyst',
  'ready.who.strategy': 'the chief', 'ready.who.units': 'the scheduler', 'ready.who.proposal': 'the scheduler',
  'ready.who.approval': 'the watch commander',
  'ready.yourTurn': 'Your turn: {0}.',
  'ready.go': 'Go there',
  'ready.waiting': 'Next up: {0}.',
  'ready.returned': 'version {0} was returned: see why in the plan',
  'ready.allDone': 'The plan is published: the shift is ready.',
  'alerts.escalateNote': 'What is needed (required to escalate)',
  'log.alert.autoescalate': 'Nobody acknowledged it in time: escalated to the watch commander',
  'log.unit.SupportRequested': 'Requested backup',
  'log.unit.UnitUnavailable': 'Reported the unit unavailable',
  'log.unit.UnitRestored': 'Reported the unit available again',
  'denial.alert.escalate_needs_note': 'To escalate the alert for {callSign} to the watch commander, say what is needed.',
  'units.m': 'meters',
  'units.ft': 'feet',
  'theme.units': 'Distances in',
  'theme.unitsAuto': 'By country ({0})',
  'theme.units.imperial': 'feet and miles',
  'theme.units.metric': 'meters and kilometers',
  'mfa.required': 'Second factor: required, checked on every sign-in token.',
  'mfa.provider': 'Second factor: the identity provider was declared to enforce it; PROTECT does not check it.',
  'mfa.off': 'Second factor: not required. CJIS requires it to access criminal justice information.',
  'denial.auth.mfa_required': 'Your agency requires a second factor and the identity provider did not confirm one for this sign-in. Sign in again using your second factor.',
  'config.rulesByChief': 'The chief changes these rules (§7.1): here they can be read, not edited.',
  'config.layersByAnalyst': 'The crime analyst loads districts and zones, from Hotspots › Layers.',
  'analyst.tabLayers': 'Layers',
  'radio.incident': 'Incident reported by radio…',
  'radio.unavailable': 'Unit unavailable…',
  'radio.restored': 'Available again',
  'radio.incidentWhat': 'What is happening (required)',
  'radio.unavailableWhy': 'Why it is unavailable (required)',
  'radio.confirm': 'Register',
  'radio.help': 'For what the unit reported by radio: it has the same effect as if it came from the phone, and the shift log says who registered it.',
  'radio.incidentDone': 'Incident registered for {0}. If it had a visit under way, that visit is on hold: request a reoptimization to hand out the rest.',
  'radio.unavailableDone': '{0} is unavailable. Request a reoptimization to hand out its visits.',
  'radio.restoredDone': '{0} is available again. Request a reoptimization to give it work.',
  'log.unit.unavailable': 'Unavailable ({source})',
  'log.unit.restored': 'Available again ({source})',
  'source.radio': 'by radio',
  'source.phone': 'from the phone',
  'denial.unit.unavailable_needs_reason': 'To mark {callSign} unavailable, say why.',
  'denial.incident.needs_description': 'To register the incident for {callSign}, say what is happening.',
  'loss.offline_expired': 'too long without a connection',
  'loss.signed_out': 'the officer signed out with records pending',
  'log.reoptimization.proposed': 'Reoptimization proposed a change: {added} in, {removed} out, {units} units out of service',
  'log.reoptimization.nochanges': 'Reoptimization found nothing to change',
  'log.reoptimization.failed': 'Reoptimization failed: the running plan stands',
  'log.reoptimization.returned': 'Revision returned for corrections',
  'log.plan.published': 'Plan {shift} version {version} approved and published ({assignments} visits; proposed by {proposedBy})',
  'log.plan.reoptimization': 'Revision {version} approved and published ({assignments} visits; requested by {proposedBy})',
  'log.plan.manual': 'Manual reassignment: version {version} signed',
  'log.handover.delivered': 'Shift {shift} handed over',
  'log.handover.received': 'Shift {shift} received',

  'handover.title': 'Shift handover',
  'handover.help': 'What you leave to whoever comes in, on the record: what is still open, what they need to know, and both names.',
  'handover.wouldHandOver': 'What you would hand over',
  'handover.counts': 'Units still out: {0} · Open assignments: {1} · Alerts: {2} · Incidents: {3}',
  'handover.critical': 'Critical alerts: {0}',
  'handover.silent': 'Units out and not reporting: {0}',
  'handover.stillOut': 'Still out',
  'handover.clean': 'Nothing open. This shift hands over clean.',
  'handover.notes': 'Anything else worth knowing (optional)',
  'handover.notesRequired': 'Something is still open: say what the next shift needs to know',
  'handover.deliver': 'Hand over the shift',
  'handover.deliveredBy': 'Handed over by {0}',
  'handover.asDelivered': 'As it stood at the handover',
  'handover.frozen': 'This is the picture at the moment it was signed, not the situation now.',
  'handover.receivedBy': 'Received by {0}',
  'handover.receiptNote': 'Anything to note on taking it (optional)',
  'handover.receive': 'Take the shift',
  'handover.waitingForOther': 'Waiting for the incoming supervisor: whoever hands over cannot also receive.',
  'handoverStatus.Delivered': 'Handed over',
  'handoverStatus.Received': 'Received',
  'denial.handover.needs_note': 'Something is still open, so the handover needs a note saying what the next shift has to know.',
  'denial.alert.support_needs_note': 'To resolve the backup request from {callSign}, note how it was handled.',
  'denial.handover.same_person': 'Whoever hands over cannot receive: a handover signed by the same person on both sides transferred nothing.',

  'map.units': 'Units',
  'map.assignments': 'Assignments',
  'map.legendUnit': 'Unit',
  'map.legendTarget': 'Assigned location',
  'map.legendAlert': 'Unit with an open deviation',
  'map.legendWarning': 'Deviation: warning',
  'map.legendCritical': 'Deviation: critical',
  'map.stack': '{0} deviations here',

  'alert.LateArrival': '{callSign} has not reached {label}: {minutes} min late.',
  'alert.ShiftNotStarted': '{callSign} has not started the shift: {visits} visits unattended, the first one {minutes} min ago.',
  'alert.ShiftNotStarted.one': '{callSign} has not started the shift: 1 visit unattended, {minutes} min ago.',
  'alert.ShortPresence': '{callSign} stayed {actual} of {planned} min at {label}.',
  'alert.OutsideJurisdiction': '{callSign} is {distance} outside the jurisdiction.',
  'alert.OutOfZone': '{callSign} is {distance} away from {label} during the visit.',
  'alert.SupportRequested': '{callSign} requests backup. {reason}',
  'alert.IncidentSighted': '{callSign} reports an incident it cannot attend: {reason}',
  'alert.UnitUnavailable': '{callSign} is out of service with {visits} released visits: reoptimize the plan to reassign them.',
  'alert.UnitUnavailable.one': '{callSign} is out of service with 1 released visit: reoptimize the plan to reassign it.',
  'alert.ConnectivityLost': 'No word from {callSign} for {minutes} min.',

  'kind.LateArrival': 'Late arrival',
  'kind.ShiftNotStarted': 'Shift not started',
  'kind.ShortPresence': 'Short presence',
  'kind.OutsideJurisdiction': 'Outside jurisdiction',
  'kind.OutOfZone': 'Out of zone',
  'kind.SupportRequested': 'Backup requested',
  'kind.IncidentSighted': 'Incident reported',
  'kind.UnitUnavailable': 'Out of service',
  'kind.ConnectivityLost': 'No connection',

  'severity.Warning': 'Warning',
  'severity.Critical': 'Critical',

  'status.New': 'New',
  'status.Acknowledged': 'Acknowledged',
  'status.Assigned': 'Assigned',
  'status.Escalated': 'Escalated',
  'status.Resolved': 'Resolved',
  'status.Closed': 'Closed',
  'status.Reported': 'Reported',
  'status.Validated': 'Validated',
  'status.Active': 'Active',
  'status.Controlled': 'Under control',
  'status.Completed': 'Completed',
  'status.Cancelled': 'Cancelled',

  'unit.OffDuty': 'Off duty',
  'unit.Available': 'Available',
  'unit.EnRoute': 'En route',
  'unit.Patrolling': 'Patrolling',
  'unit.IncidentResponse': 'Handling incident',
  'unit.Returning': 'Returning',
  'unit.Unavailable': 'Unavailable',
  'unit.EndShift': 'Shift closed',

  'connectivity.Online': 'Online',
  'connectivity.Stale': 'Delayed',
  'connectivity.Offline': 'Offline',
  'connectivity.Never': 'Never reported',

  'assignment.Released': 'Released',
  'assignment.Acknowledged': 'Received',
  'assignment.InProgress': 'At the location',
  'assignment.Completed': 'Completed',

  // Motivos de rechazo (Protect.Contracts.Common.DenialCodes)
    'license.evaluation': 'No licence installed: evaluation limits apply.',
    'license.expired': 'The licence expired on {0}. The shift keeps running; new planning is blocked.',
    'license.grace': 'The licence expired on {0}. It keeps working for now — renew it.',
    'license.invalid': 'The licence could not be validated: evaluation limits apply.',
    'license.expiring': 'The licence expires on {0}, in {1} {1|day|days}.',
    'license.soldTo': 'Licensed to {0}',
    'denial.license.agency_not_covered': 'Agency {agency} is not in this deployment’s licence.',
    'denial.license.units_exceeded': 'The licence covers {max} units per shift and the shift already has {formed}.',
    'denial.license.module_not_covered': 'The {module} module is not part of this licence.',
    'denial.license.expired': 'The licence expired on {expiredOn} and the grace period is over.',
    'nav.command': 'Command Center',
    'nav.analyst': 'Hotspots',
    'nav.planner': 'Planning',
    'analyst.help': 'Where the crime is. Calculate a set, look at it, and publish the one the shift should use.',
    'analyst.calculate': 'New calculation',
    'analyst.calculateHelp': 'Same inputs produce the same set: a calculation stays auditable months later.',
    'analyst.name': 'Name for this set',
    'analyst.from': 'From',
    'analyst.to': 'To',
    'analyst.method': 'Method',
    'analyst.method.grid_count': 'Count per cell',
    'analyst.method.kde': 'Kernel density (KDE)',
    'analyst.window': 'Time window',
    'analyst.window.ALL_DAY': 'All day',
    'analyst.top': 'How many hotspots',
    'analyst.windowWarning': 'The time window matters: morning and night hotspots overlap by about a fifth. Calculate one set per shift.',
    'analyst.run': 'Calculate',
    'analyst.sets': 'Sets',
    'analyst.noSets': 'No sets yet.',
    'analyst.incidents': 'incidents',
    'analyst.createdBy': 'Calculated by {0}',
    'analyst.fewer': 'You asked for {0}: only {1} places reach the minimum of {2} incidents in this window and period. For more, widen the period.',
    'analyst.publishedBy': 'Published by {0}',
    'analyst.publish': 'Publish',
    'analyst.hotspots': 'Hotspots',
    'analyst.fingerprint': 'Input fingerprint {0}…',
    'analyst.hotspotLine': '{0} {0|incident|incidents} · risk {1} · stay {2} min',
    'analyst.beat': 'Beat {0}',
    'setStatus.Draft': 'Draft',
    'setStatus.Candidate': 'Candidate',
    'setStatus.Published': 'Published',
    'setStatus.Retired': 'Superseded',
    'priority.High': 'High',
    'priority.Medium': 'Medium',
    'priority.Low': 'Low',
    'denial.hotspots.forbidden': 'You are not allowed to do that.',
    'planner.help': 'What the shift has, what the optimizer proposes, and which scenario becomes the plan.',
    'planner.shift': 'Shift',
    'planner.resources': 'What the shift has',
    'planner.officers': '{0} {0|officer|officers} available',
    'planner.vehicles': '{0} {0|vehicle|vehicles} available',
    'planner.units': 'Units · {0}',
    'planner.noUnits': 'No units formed for this shift yet.',
    'planner.plan': 'Plan',
    'planner.noPlan': 'No published plan for this shift.',
    'planner.planLine': 'Version {0} · {1} assignments · proposed by {2}',
    'planner.approvedBy': 'Approved by {0}',
    'planner.pending': 'Version {0} is waiting for approval, proposed by {1}',
    'planner.returned': 'Version {0} was sent back by {1} for corrections:',
    'planner.acceptPartial': 'I accept that it is partial: {0} hotspots get no visit',
    'revision.title': 'Revision {0} awaiting approval',
    'revision.requested': 'Requested by {0} at {1}: {2}',
    'revision.coverage': 'Coverage {0} % → {1} %',
    'revision.kept': '{0} untouched (done, in progress, already released, leaving within minutes or outside the replanned divisions) · {1} planned kept as they were',
    'revision.excluded': 'Left out: {0}',
    'revision.notStarted': 'Did not start the shift ({0}), left out: {1}',
    'revision.andMore': '{0} and {1} more',
    'revision.cancelDisclosed': 'Withdrawn from officers who already had them ({0}):',
    'revision.reassign': 'Interrupted visits that are closed; their hotspot goes back into the plan if time remains ({0}):',
    'revision.removed': 'Planned visits cancelled ({0}):',
    'revision.added': 'New visits ({0}):',
    'revision.acceptPartial': 'I accept that the plan is partial: some hotspots get no visit',
    'revision.stale': 'This revision no longer matches the plan and cannot be published: {0}',
    'revision.sameAsReturned': 'This is the same proposal as revision {0}, which {1} returned: «{2}». The run changed nothing of what was objected to. If you return it again, say what the engine should do differently (units, scope, time window).',
    'revision.requestAgain': 'Request a new one with the same reason',
    'revision.otherApprover': 'You requested this revision, and the agency requires another person to approve it (four eyes). You can still return it.',
    'revision.waiting': 'The watch commander decides it: approving publishes it and the phones receive their part.',
    'note.byManual': 'superseded by manual adjustment {0}',
    'note.byReoptimization': 'superseded by reoptimization {0}',
    'note.byVersion': 'superseded by version {0}',
    'note.byLaterProposal': 'superseded by a later proposal',
    'note.byLaterReoptimization': 'superseded by a later reoptimization',
    'planner.approve': 'Approve and publish',
    'planner.reject': 'Return for changes',
    'planner.rejectReason': 'What has to change',
    'planner.propose': 'Propose this scenario',
    'planner.proposedAs': 'Proposed as version {0}: waiting for the watch commander',
    'planner.scenarios': 'Scenarios · {0}',
    'planner.noScenarios': 'No scenarios yet for this shift.',
    'planner.generate': 'New scenario',
    'planner.preset': 'Baseline',
    'planner.fromProfile': 'as in the profile',
    'planner.fromProfileHelp': 'What you leave as «as in the profile» is what command approved. Change only what you want to try.',
    'planner.preset.base': 'Baseline, no improvements',
    'planner.preset.protect-base': 'PROTECT baseline',
    'planner.rule': 'Division rule',
    'planner.rule.prefer': 'Prefer own division',
    'planner.rule.strict': 'Own division only',
    'planner.rule.ignore': 'Ignore divisions',
    'planner.divisionKnobs': '{0}: revisit {1} min, dwell ×{2}',
    'planner.revisit': 'Revisit (min)',
    'planner.hourly': 'Hourly risk',
    'planner.lookahead': 'Lookahead',
    'planner.reserve': 'Response reserve',
    'planner.run': 'Generate',
    'planner.crewSize': '{0} {0|officer|officers}',
    'planner.needsAvailability': 'No availability has been published for this shift: ask HR (officers) and Fleet (vehicles). Without it there are no units to form.',
    'planner.needsUnits': 'No units have been formed for this shift yet: form them under Units, then generate.',
    'planner.kpis': 'Coverage {0}% · risk-weighted {1}% · {2}/{3} visits · use {4}%',
    'planner.knobs': 'Hourly risk {0} · lookahead {1} · reserve {2} · revisit {3} min',
    'planner.on': 'on',
    'planner.off': 'off',
    'planner.partial': 'Partial: {0} hotspots with no visit',
    'planner.crossDivision': '{0} {0|visit|visits} outside the division',
    'planner.tip': 'Generate two and compare: the numbers are what the decision rests on.',
    'assignment.Planned': 'Planned',
    'assignment.Interrupted': 'Interrupted',
    'assignment.Missed': 'Missed',
    'assignment.Reassigned': 'Reassigned',
    'assignment.Cancelled': 'Cancelled',
    'crew.title': 'Form a unit',
    'crew.help': 'Pick the crew and the vehicle. The rules are checked as you go, before anything is saved.',
    'crew.type': 'Unit type',
    'crew.callSign': 'Call sign',
    'crew.division': 'Division',
    'crew.officers': 'Crew · {0} of {1}',
    'crew.vehicle': 'Vehicle',
    'crew.pickVehicle': 'Choose a vehicle',
    'crew.taken': 'on {0}',
    'crew.years': '{0} yrs',
    'crew.clean': 'No rule is broken.',
    'crew.blocked': 'This cannot be formed, with or without authorisation.',
    'crew.needsException': 'Needs an authorised exception.',
    'crew.exceptionReason': 'Why it is authorised',
    'crew.cannotExcept': 'Only shift authority can sign an exception. Ask the watch commander.',
    'crew.form': 'Form the unit',
    'crew.formed': 'Unit {0} formed.',
    'severity.Soft': 'Warning',
    'severity.Hard': 'Needs exception',
    'severity.Blocking': 'Not allowed',
    'rule.CREW_SIZE': 'Crew size',
    'rule.OFFICER_INACTIVE': 'Officer not active',
    'rule.OFFICER_NOT_AVAILABLE': 'Officer not available',
    'rule.OFFICER_DOUBLE_BOOKED': 'Officer already on another unit',
    'rule.VEHICLE_NOT_AVAILABLE': 'Vehicle not available',
    'rule.VEHICLE_DOUBLE_BOOKED': 'Vehicle already on another unit',
    'rule.OFFICER_RESTRICTED': 'Officer on restricted duty',
    'rule.VEHICLE_TYPE_MISMATCH': 'Wrong vehicle type',
    'rule.ALL_ROOKIE_CREW': 'All-rookie crew',
    'rule.MISSING_CERTIFICATION': 'Missing certification',
    'rule.OUTSIDE_HOME_DIVISION': 'Outside home division',
    'rule.VEHICLE_OUTSIDE_BASE_DIVISION': 'Vehicle outside base division',
    'ruleDetail.CREW_SIZE': 'Type {type} takes {min} to {max} officers; {count} were proposed.',
    'ruleDetail.OFFICER_INACTIVE': 'Officer {badge} is not active.',
    'ruleDetail.OFFICER_NOT_AVAILABLE.unpublished': 'No availability published for officer {badge} on {shift}.',
    'ruleDetail.OFFICER_NOT_AVAILABLE.absent': 'Officer {badge} is absent on {shift}.',
    'ruleDetail.OFFICER_RESTRICTED': 'Officer {badge} is on restricted duty on {shift}.',
    'ruleDetail.OFFICER_DOUBLE_BOOKED': 'Officer {badge} is already on unit {unit} for {shift}.',
    'ruleDetail.OUTSIDE_HOME_DIVISION': 'Officer {badge} belongs to {home}, not to {division}.',
    'ruleDetail.ALL_ROOKIE_CREW': 'Every crew member has under {years} year(s) of service.',
    'ruleDetail.MISSING_CERTIFICATION': 'No crew member holds the {certification} certification.',
    'ruleDetail.VEHICLE_NOT_AVAILABLE.status': 'Vehicle {vehicle} cannot be used: {status}.',
    'ruleDetail.VEHICLE_NOT_AVAILABLE.absent': 'Vehicle {vehicle} is not available on {shift}.',
    'ruleDetail.VEHICLE_DOUBLE_BOOKED': 'Vehicle {vehicle} is already assigned to {unit} for {shift}.',
    'ruleDetail.VEHICLE_TYPE_MISMATCH': 'Type {type} takes a {required} vehicle; {vehicle} is a {actual}.',
    'ruleDetail.VEHICLE_OUTSIDE_BASE_DIVISION': 'Vehicle {vehicle} is based in {base}, not in {division}.',
    'denial.formation.exception_not_authorized': 'Only shift authority can sign an exception ({rules}).',
    'crew.pickVehicleFirst': 'Choose a vehicle to check the rules.',
    'crew.needCallSign': 'Type the unit call sign to form it.',
    'reason.LEAVE': 'leave',
    'reason.TRAINING': 'training',
    'reason.LIGHT_DUTY': 'light duty',
    'reason.MAINTENANCE': 'maintenance',
    'crew.cannotCheck': 'The rules could not be checked right now. Try again before forming.',
    'vehicle.Available': 'Available',
    'vehicle.Assigned': 'Assigned',
    'vehicle.Maintenance': 'In maintenance',
    'rule.shift': 'the {0} shift of {1}',
    'vehicle.OutOfService': 'Out of service',
    'compare.title': 'Compare',
    'compare.pick': 'Compare',
    'compare.hint': 'Tick two scenarios to compare them.',
    'compare.changed': 'What is different',
    'compare.result': 'What came out',
    'compare.objective': 'what the optimizer maximizes',
    'compare.same': 'Same settings in both: the difference is in the run, not in the knobs.',
    'compare.wins': 'better',
    'kpi.coverage': 'Coverage',
    'kpi.riskWeightedCoverage': 'Risk-weighted coverage',
    'kpi.visitRating': 'Visit rating',
    'kpi.utilization': 'Unit use',
    'kpi.visits': 'Visits',
    'kpi.expectedVisits': 'Expected visits',
    'kpi.hotspotsNotServed': 'Hotspots with no visit',
    'kpi.crossDivisionVisits': 'Visits outside the division',
    'knob.revisitMinutes': 'Revisit period (min)',
    'knob.toleranceMinutes': 'Late tolerance (min)',
    'knob.defaultDwellMinutes': 'Default stay (min)',
    'knob.dwellFactor': 'Stay factor (× recommended)',
    'knob.alpha': 'Risk vs. fairness (alpha)',
    'knob.gamma': 'Delay scale (gamma)',
    'knob.divisionRule': 'Division rule',
    'knob.maxTravelMinutes': 'Max travel (min)',
    'knob.roadFactor': 'Road factor',
    'knob.useHourlyRisk': 'Hourly risk',
    'knob.useLookahead': 'Lookahead',
    'knob.useResponseReserve': 'Response reserve',
    'knob.reserveShare': 'Reserve share',
    'knob.lookaheadMinutes': 'Lookahead (min)',
    'knob.minReservePerDivision': 'Minimum reserve per division',
    'nav.fleet': 'Fleet',
    'nav.hr': 'Personnel',
    'fleet.help': 'The vehicle catalogue and which ones the shift can count on.',
    'fleet.catalogue': 'Vehicles · {0}',
    'fleet.statusHelp': 'Sending a vehicle to the shop does not pull it from units that already have it: that is a reoptimisation, and command decides it.',
    'fleet.history': 'History',
    'fleet.closeHistory': 'Close history',
    'fleet.historyTitle': 'Where {0} has been',
    'fleet.neverOut': 'This vehicle has not been on a unit yet.',
    'fleet.historySummary': '{0} {0|shift|shifts} worked · {1} of {2} assignments completed',
    'fleet.historyRange': 'From {0} to {1}',
    'fleet.shift': 'Shift',
    'fleet.unit': 'Unit',
    'fleet.crew': 'Crew',
    'fleet.work': 'Done',
    'fleet.status': 'Vehicle status',
    'hr.help': 'Who the shift can count on, and the planning profile of each officer.',
    'hr.roster': 'Officers · {0}',
    'hr.rosterHelp': 'Only the planning profile: badge, rank, division, seniority and certifications. Personal data stays in HR.',
    'hr.active': 'Active',
    'resources.availability': 'Availability for the shift',
    'resources.replaces': 'Publishing replaces the whole list: {0} in the shift right now. Anyone left out stops being available.',
    'resources.notInShift': 'Not in this shift',
    'resources.status': 'Availability',
    'resources.reason': 'Reason',
    'resources.publish': 'Publish availability',
    'resources.nothingToPublish': 'No changes since it was published.',
    'resources.published': 'Published: {0} in the shift. Planning sees it now.',
    'resources.needsReason': 'An absence needs a reason: whoever plans cannot cover what has no explanation.',
    'availability.Available': 'Available',
    'availability.Absent': 'Absent',
    'availability.Restricted': 'Restricted duty',
    'availability.vehicle.Available': 'Available',
    'availability.vehicle.Absent': 'Not available (shop or other)',
    'availability.vehicle.Restricted': 'Limited use',
    'resources.needsReasonVehicle': 'A vehicle that cannot go out needs a reason: whoever plans has to know why it is missing.',
    'nav.admin': 'Administration',
    'admin.help': 'The phones this agency operates with, and the record of who authorised what.',
    'admin.deployment': 'This deployment',
    'admin.provider': 'Identity provider: {0}',
    'admin.noProvider': 'No identity provider configured yet.',
    'admin.demo': 'Demonstration mode: development tokens and the simulator are available.',
    'admin.license': 'Licence: {0} · {1} · until {2}',
    'admin.deadLetters': '{0} messages were never processed ({1}), the latest on {2}: someone believes that work was done. Check the server log.',
    'admin.deadLetters.one': 'A message was never processed ({1}), on {2}: someone believes that work was done. Check the server log.',
    'admin.usersElsewhere': 'Users and roles are not managed here: they live in the identity provider of the agency, so an officer who leaves the force loses access when the department removes them, with no second list to remember.',
    'admin.devices': 'Phones · {0}',
    'admin.devicesHelp': 'A valid sign-in on an unregistered phone does not get in. Revoking one stops it operating from the next call.',
    'admin.deviceId': 'Phone id',
    'admin.deviceLabel': 'Label (who carries it)',
    'admin.register': 'Register',
    'admin.registeredAt': 'Registered {0}',
    'admin.revokedAt': 'Revoked {0} · {1}',
    'admin.revokeReason': 'Why it is revoked',
    'admin.revoke': 'Revoke',
    'admin.audit': 'Record',
    'admin.auditHelp': 'One timeline, newest first. This is what answers, two years from now, why that exception was authorised.',
    'admin.auditEmpty': 'Nothing recorded yet.',
    'admin.by': 'by {0}',
    'deviceStatus.Active': 'Active',
    'deviceStatus.Revoked': 'Revoked',
    'licenseState.Evaluation': 'evaluation',
    'licenseState.Active': 'active',
    'licenseState.Grace': 'in grace period',
    'licenseState.Expired': 'expired',
    'licenseState.Invalid': 'not valid',
    'audit.formation.exception': 'Formation exception',
    'audit.plan.proposed': 'Plan proposed',
    'audit.plan.published': 'Plan approved and published',
    'audit.plan.rejected': 'Plan rejected',
    'audit.plan.superseded': 'Plan superseded',
    'audit.device.registered': 'Phone registered',
    'audit.device.revoked': 'Phone revoked',
    'audit.device.reregistered': 'Phone registered again',
    'admin.auditFilter': 'Filter',
    'admin.auditAll': 'Everything',
    'admin.auditExceptions': 'Formation exceptions',
    'admin.auditPlans': 'Plans',
    'admin.auditDevices': 'Phones',
    'ingest.title': 'Reports',
    'ingest.help': 'Bringing in the department reports. It takes minutes and runs in the background: you can leave the screen.',
    'ingest.source': 'Source',
    'ingest.run': 'Bring in reports',
    'ingest.running': 'An ingestion is running. The history updates on its own; you can leave the screen.',
    'ingest.window': 'Download a wider window than you plan to analyse: an incident reported late falls outside a tight window.',
    'ingest.none': 'Nothing brought in yet.',
    'ingest.line': '{0} rows written of {1} read · {2} skipped',
    'ingest.skipped': 'Skipped rows have no source identifier: without a natural key, bringing them in again would duplicate them.',
    'ingestStatus.RUNNING': 'Running',
    'ingestStatus.COMPLETED': 'Completed',
    'ingestStatus.FAILED': 'Failed',
    'ingestStatus.INTERRUPTED': 'Interrupted',
    'ingest.interrupted': 'The server restarted while this ran. What was written is valid: bring in the same period again to finish it.',
    'demo.formShift': 'Form the units for this shift',
    'demo.formed': '{0} {0|unit|units} formed.',
    'demo.formedWithSkips': '{0} units formed, {1} could not be: {2}',
    'coverage.title': 'Does the shift add up?',
    'coverage.help': 'Per division, because a hundred spare officers in one division do not cover a gap in another.',
    'coverage.none': 'The agency has no staffing targets loaded, or no availability published for this shift.',
    'coverage.division': 'Division',
    'coverage.officers': 'Officers',
    'coverage.sergeants': 'Sergeants',
    'coverage.vehicles': 'Vehicles',
    'coverage.formed': 'Formed',
    'coverage.ok': 'covered',
    'coverage.shortOfficers': '{0} {0|officer|officers} short',
    'coverage.shortSergeants': '{0} {0|sergeant|sergeants} short',
    'coverage.shortVehicles': '{0} vehicles short',
    'coverage.aside': '{0} restricted · {1} absent',
    'config.title': 'Agency configuration',
    'config.help': 'How this agency is organised. It is not data of the algorithm nor of the shift, and only the agency knows it.',
    'config.staffing': 'Staffing target',
    'config.staffingHelp': 'How many positions and units each division fields per shift. It is what answers «does the shift add up?».',
    'config.sectors': 'Sectors and divisions',
    'config.sectorsHelp': 'Which division answers for each beat prefix. Without it the optimizer cannot tell whose hotspot is whose.',
    'config.division': 'Division',
    'config.shift': 'Shift',
    'config.positions': 'Positions',
    'config.oneOfficer': '1-officer units',
    'config.twoOfficer': '2-officer units',
    'config.sergeantUnits': 'Sergeants',
    'config.sector': 'Sector',
    'config.add': 'Add a row',
    'config.remove': 'Remove',
    'config.save': 'Save',
    'config.replaces': 'Saving replaces the whole table: a row you remove stops existing.',
    'policy.title': 'Rules of the agency',
    'policy.help': 'How much this agency tolerates, and who signs a plan. Every change is logged with its reason.',
    'policy.deviation': 'Deviation thresholds',
    'policy.deviationHelp': 'When a unit is marked as off plan. They take effect from the next shift, never in the middle of one.',
    'policy.late': 'Tolerated delay (minutes)',
    'policy.presence': 'Minimum presence (fraction of the planned stay)',
    'policy.geofence': 'Geofence ({0})',
    'policy.current': 'In force now, including the shift under way: {0}.',
    'policy.effective': 'A change saved now takes effect at {0}.',
    'policyLog.rookie': 'rookie under {0} {0|year|years}',
    'policyLog.certs': '{0} requires {1}',
    'policyLog.approval.none': 'no approval needed',
    'policyLog.approval.any': 'approval by anyone with authority',
    'policyLog.approval.four_eyes': 'approval by someone other than the proposer (four eyes)',
    'policy.pending': 'Already scheduled for {0}: {1}.',
    'policy.approval': 'Plan approval',
    'policy.approvalHelp': 'Who may publish a plan. Loosening it is an authority decision, and it is logged as one.',
    'policy.formation': 'Crew formation',
    'policy.formationHelp': 'What this agency tolerates when putting a crew together: a warning is recorded, an exception needs a watch commander, and not allowed means not allowed.',
    'policy.rookie': 'Rookie below (years of service)',
    'policy.blockingAlways': 'Absent, inactive or double-booked officers and vehicles are never allowed: no setting changes that.',
    'policy.certsFor': 'Certifications required on {0}',
    'policy.formationEffective': 'Applies from the next unit formed. Units already formed keep the version they were formed under.',
    'policy.requires': 'The plan needs approval before it is published',
    'policy.different': 'Whoever approves must not be whoever proposed (four eyes)',
    'policy.differentHelp': 'Turn this off only where the same person plans and commands: otherwise nobody can publish.',
    'policy.reason': 'Reason',
    'policy.reasonHelp': 'It is what explains, months later, why that shift was measured differently.',
    'policy.reasonHelpApproval': 'It is what explains, months later, who could sign a plan and why.',
    'policy.reasonHelpFormation': 'It is what explains, months later, why a crew was accepted or needed an exception.',
    'policy.reasonNeeded': 'Write the reason before saving.',
    'policy.history': 'Log of changes',
    'policy.noHistory': 'Nothing has been changed: the factory rules are in force.',
    'policy.when': 'When',
    'policy.who': 'Who',
    'policy.what': 'What was set',
    'policy.why': 'Why',
    'admin.auditPolicies': 'Rule changes',
    'admin.auditBranding': 'Theme',
    'admin.auditRoster': 'Roster and fleet imports',
    'audit.branding.changed': 'Agency theme changed',
    'audit.roster.officer.imported': 'Roster imported',
    'audit.roster.vehicle.imported': 'Fleet imported',
    'audit.policy.deviation': 'Thresholds changed',
    'audit.policy.approval': 'Approval rule changed',
    'audit.approval.none': 'The scheduler publishes without approval',
    'audit.approval.four_eyes': 'A second person approves (four eyes)',
    'audit.approval.any': 'Anyone approves, including whoever proposed it',
    'app.noAreas': 'This sign-in opens no screen in the Command Center. The roles it carries work elsewhere — the officer, for instance, works from the phone.',
    'role.chief': 'Chief',
    'role.watch-commander': 'Watch commander',
    'role.command': 'Command center',
    'role.planner': 'Planner',
    'role.analyst': 'Analyst',
    'role.fleet': 'Fleet',
    'role.hr': 'HR',
    'role.admin': 'Administrator',
    'storage.undeclared': 'Encryption at rest: not declared. Treated as absent — declare it after encrypting the volume.',
    'storage.none': 'Encryption at rest: none. Shift data, the location of every officer and the incident history are readable to whoever reaches the disk or a backup.',
    'storage.volume': 'Encryption at rest: encrypted volume.',
    'storage.managed': 'Encryption at rest: managed service.',
    'quality.title': 'Data quality',
    'quality.help': 'An incident with defects is not dropped on ingestion: it is kept flagged, so it can be listed and explained here. What is dropped becomes invisible, and what is invisible never gets fixed.',
    'quality.empty': 'No incidents loaded yet. Import a source first.',
    'quality.usable': '{0} incidents can feed a calculation ({1} of what is loaded).',
    'quality.breakdown': '{0} clean · {1} usable with reservations · {2} not usable',
    'quality.defects': 'Defects',
    'quality.defect': 'Defect',
    'quality.count': 'How many',
    'quality.defectsHelp': 'An incident can carry more than one, so the numbers do not add up to the total.',
    'quality.sources': 'Sources',
    'quality.sourcesHelp': 'What each one brought and until when. The last date answers whether the source is still alive.',
    'quality.source': 'Source',
    'quality.unusableShort': 'Not usable',
    'quality.from': 'From',
    'quality.until': 'Until',
    'quality.samples': 'Rows to look at',
    'quality.samplesHelp': 'Nobody fixes a percentage: you fix the row you can see. Worst first.',
    'qualityStatus.Ok': 'clean',
    'qualityStatus.Review': 'with reservations',
    'qualityStatus.Unusable': 'not usable',
    // Por qué una fila no entró (§16.3). Códigos desde el servidor; la frase la pone la pantalla.
  'skip.no_external_id': 'no source identifier',
  'skip.unreadable': 'could not be read',
  'ingest.why': 'Of those skipped:',
  'ingest.flags': 'Of those written, with something missing:',
  'ingest.clean': 'Everything written came in complete.',

  'flag.MissingGeo': 'no coordinates',
    'flag.OutOfBounds': 'outside the agency area',
    'flag.MissingOccurredAt': 'no date of occurrence',
    'flag.ApproximateTime': 'time filled in by the source',
    'flag.NonNibrsCode': 'offence outside NIBRS',
    'flag.LowGeocodeScore': 'weak geocoding',
    'explorer.title': 'Incident explorer',
    'explorer.help': 'What lies under a hotspot. The results are drawn on the map, small and underneath: the question is whether the point has behind it what it claims to have.',
    'explorer.from': 'From',
    'explorer.to': 'To',
    'explorer.tier': 'Patrol tier',
    'explorer.quality': 'Quality',
    'explorer.any': 'Any',
    'explorer.search': 'Search',
    'explorer.window': 'Time of day (agency time)',
    'explorer.allDay': 'All day',
    'explorer.windowNeedsPeriod': 'To look at a time of day, choose from and to as well.',
    'explorer.found': '{0} incidents.',
    'explorer.truncated': 'Showing {0} of {1}. Narrow the period to see the rest.',
    'explorer.listCut': 'The list shows the 40 most recent of {0}; the map shows them all.',
    'explorer.inexactHelp': 'The source filled in the time: it does not count for analysis by time of day.',
    'tier.StreetCrime': 'Street crime',
    'tier.PublicOrder': 'Public order',
    'tier.IndoorDomestic': 'Domestic violence',
    'tier.Excluded': 'Not deterred by presence',
    'analyst.tabData': 'Data',
    'analyst.tabIncidents': 'Incidents',
    'analyst.tabHotspots': 'Hotspots',
    'changes.title': 'What changed · v{0} → v{1} of {2}',
    'changes.differentGrid': 'The two runs used different grids, so points are matched by proximity within {0} m. A point that only shifted cells may show up as new.',
    'changes.stability': 'Stability',
    'changes.stabilityHelp': 'How much of the map the two versions share. Low stability can mean crime moved — or that the method is unstable with this much data. Telling those apart is the analyst’s job.',
    'changes.counts': '{0} kept · {1} new · {2} gone · {3} of the new set was already there',
    'changes.inputs': 'What changed in the inputs',
    'changes.period': 'Period',
    'changes.incidents': 'Incidents in',
    'changes.method': 'Method',
    'changes.window': 'Time window',
    'changes.moved': 'Moved · {0}',
    'changes.movedHelp': 'Points that crossed at least five places. A point that moved up changed someone’s shift.',
    'changes.riskDelta': 'Risk {0} → {1}',
    'changes.new': 'New · {0}',
    'changes.newHelp': 'Not in the previous version: this is where patrol goes that did not go before.',
    'changes.dropped': 'Gone · {0}',
    'changes.droppedHelp': 'In the previous version and not in this one: these places stop being patrolled.',
    'changes.point': '{0} incidents · beat {1}',
    'changes.same': 'Same points, same order. Only the numbers behind them moved.',
    'changes.open': 'What changed',
    'changes.close': 'Close comparison',
    'admin.tabDeployment': 'Deployment',
    'admin.tabAgency': 'Agency',
    'admin.tabDevices': 'Phones',
    'admin.tabAudit': 'Record',
    'admin.tabTheme': 'Theme',
    'nav.insight': 'Performance',
    'insight.help': 'What was planned against what happened, shift by shift. Nothing here changes the shift under way: it is for learning from the last one.',
    'insight.caveat': 'These numbers are worth what the execution that produced them is worth. They measure compliance, not effectiveness: whether patrolling brought crime down is a causal claim and needs a methodology agreed with the department.',
    'insight.simulated': 'Demonstration deployment: these shifts were run by the simulator, so the numbers describe the simulator and not a police force.',
    'insight.shifts': 'Shifts',
    'insight.empty': 'Nothing recorded yet. A shift appears here once its plan is published.',
    'insight.shift': 'Shift',
    'insight.planned': 'Planned',
    'insight.released': 'Released',
    'insight.completed': 'Completed',
    'insight.missed': 'Missed',
    'insight.deferred': 'Deferred',
    'insight.onTime': 'On time',
    'insight.presence': 'Stay',
    'insight.deviations': 'Deviations',
    'insight.scenariosGenerated': '{0} {0|scenario|scenarios} evaluated for this shift before approving the plan',
    'insight.commandActivity': 'Support requests: {0} · Plan versions returned: {1} · Manual adjustments: {2}',
    'insight.open': 'Review',
    'insight.review': 'Review of {0} · {1}',
    'insight.reviewHelp': 'Not a total: who did what. It is what feeds back into the method and the next plan.',
    'insight.approvedBy': 'Approved by {0}',
    'insight.partial': 'partial scenario, accepted by the authority',
    'insight.resources': '{0} officers available · {1} absent · {2} restricted · {3} vehicles',
    'insight.deviationBreakdown': '{0} late · {1} short stay · {2} out of zone · {3} connection lost · {4} shift not started · {5} outside jurisdiction',
    'insight.reoptimizations': '{0} reoptimization runs, {1} proposed a change',
    'insight.reoptimizationCost': 'The engine took {0} ms per run',
    'insight.coverageRecovered': 'recovered {0}{1} points of risk-weighted coverage',
    'insight.units': 'By unit',
    'insight.unit': 'Unit',
    'insight.assignments': 'Assignments',
    'insight.late': 'Average delay',
    'insight.stay': 'Stay / planned',
    'insight.incidents': 'Incidents during the shift',
    'insight.incidentsHelp': 'Time a unit spent off plan attending something else.',
    'insight.open2': 'still open',
    'link.live': 'Live',
    'link.live.help': 'Connected to the server: what you see updates on its own.',
    'link.connecting': 'Reconnecting',
    'link.connecting.help': 'The connection dropped and is being retried. What is on screen may be out of date.',
    'link.offline': 'No connection',
    'link.offline.help': 'No connection to the server. What is on screen is the last thing that arrived and is not updating.',
    'insight.rebuild': 'Rebuild history',
    'insight.rebuildHelp': 'Performance is built from events, so it only knows what happened since the module was installed. This reconstructs the past year from what TACTIX and SENTINEL kept. It erases and rebuilds, so it can be run again without counting twice.',
    'insight.rebuilt': '{0} shifts rebuilt from {1} events.',
    'insight.emptyRebuild': 'If this installation was already operating before, rebuild the history.',
    'planner.tabShift': 'Shift',
    'planner.tabUnits': 'Units',
    'planner.tabScenarios': 'Scenarios',
    'planner.tabPlan': 'Plan',
    'trends.period': '{0} to {1} · {2} shifts',
    'trends.against': 'Compared against the previous {0} shifts, of the same length.',
    'trends.noPrevious': 'Nothing before this period to compare against.',
    'trends.flat': 'no change',
    'trends.export': 'Download sheet',
    'trends.compliance': 'Compliance',
    'trends.onTime': 'On time',
    'trends.presence': 'Stay',
    'trends.work': 'Work of the period',
    'trends.planned': '{0} assignments planned',
    'trends.released': '{0} released to a unit',
    'trends.completed': '{0} completed',
    'trends.incidents': '{0} incidents · {1} min off plan',
    'trends.kinds': 'Which deviation dominates',
    'trends.kindsHelp': 'The one that repeats is the one worth acting on.',
    'trends.series': 'Shift by shift',
    'trends.seriesHelp': 'Each bar is one shift: the height is punctuality, and amber means something was left deferred. Hover to read it.',
    'trends.byShift': 'By shift of the day',
    'trends.byShiftHelp': '«Which shift gets hard for us» is a staffing question, not one about people.',
    'trends.byDivision': 'By division',
    'trends.byDivisionHelp': 'The division that fares worst is not always the one with the most work.',
    'trends.noDivision': 'no division',
    'trends.shifts': 'Shifts',
    // Lo que se observa detrás de un punto problemático (§16.3). Hipótesis, no causas: el texto
    // lo dice, porque «la regla X causa esto» invita a cambiar X sin mirar nada más.
    'cause.title': 'What shows up behind it',
    'cause.hypothesis': 'What the data shows, not a proven cause. Someone with the context decides.',
    'cause.late_release': 'released {0} min after it was already time to be there — that is the release lead, not the plan',
    'cause.travel_underestimated': 'the plan gives it {0} min less than the unit actually takes to get there — that is the travel estimate',
    'cause.first_of_shift': 'almost always the first visit of the shift: the unit starts the shift already behind',
    'cause.one_shift': 'concentrated in one shift of the day: that points at its staffing, not at the point',
    'cause.cases': '({0} of the late arrivals)',
    'cause.none': 'Nothing repeats clearly enough to point at one thing.',

    'trends.spots': 'Points we keep arriving late to',
    'trends.spotsHelp': 'A point with repeated late arrivals is usually misplaced in the plan — too far for the window it was given. That is fixed by planning, not by demanding.',
    'trends.spot': 'Point',
    'trends.visits': 'Visits',
    'trends.lateVisits': 'Late',
    'trends.short': 'Short stay',
    'trends.units': 'Units with most deviations',
    'trends.unitsHelp': 'This is not a score for people. A unit that is always late is usually telling you the travel does not fit in the plan. Read it as a question, not a verdict.',
    'insight.tabTrends': 'Trends',
    'insight.tabShifts': 'Shifts',
    'nav.strategy': 'Strategy',
    'strategy.help': 'How this agency patrols: the approach, the analytical method, the tactic and the goals it will be measured against. It is approved once and applied to every shift, instead of each shift being planned with whatever knobs someone remembered that day.',
    'compareProfiles.open': 'Compare',
    'compareProfiles.close': 'Close comparison',
    'compareProfiles.title': 'Comparing {0} against {1}',
    'compareProfiles.differentPeriods': 'Only one profile is in force at a time, so these two never covered the same shifts. This is not an experiment: different weeks bring different crime, weather and staffing. Read the goals first and the raw numbers last.',
    'compareProfiles.ownGoals': 'What each one promised, and what it delivered',
    'compareProfiles.ownGoalsHelp': 'This is the only fair comparison: each profile set its own bar, so meeting it means the same thing for both. Reaching a modest goal is not better than falling just short of an ambitious one.',
    'compareProfiles.noGoals': 'Neither profile set any goal, so there is nothing to hold them to.',
    'compareProfiles.goal': 'Goal (actual / target)',
    'compareProfiles.conditions': 'What they worked with',
    'compareProfiles.period': 'Period',
    'compareProfiles.shifts': 'Shifts planned',
    'compareProfiles.officers': 'Officers available per shift',
    'compareProfiles.absent': 'Officers absent per shift',
    'compareProfiles.incidents': 'Incidents per shift',
    'compareProfiles.incidentMinutes': 'Minutes off plan per shift',
    'compareProfiles.outcome': 'What came out',
    'compareProfiles.planned': 'Assignments planned',
    'compareProfiles.released': 'Released to a unit',
    'compareProfiles.completed': 'Completed',
    'compareProfiles.deferred': 'Deferred',
    'compareProfiles.onTime': 'On time',
    'compareProfiles.presence': 'Stay',
    'compareProfiles.late': 'Late arrivals',
    'compareProfiles.shortPresence': 'Short stays',
    'compareProfiles.footnote': 'A profile with few shifts behind it says less than one with many: the number of shifts is the weight of everything above.',
    'strategy.active': 'Profile in force',
    'strategy.none': 'This agency has not approved a profile yet. Shifts are planned with the factory preset.',
    'strategy.method': 'Method: {0} · window {1} · {2} hotspots · {3} days of history',
    'strategy.tactic': 'Tactic: {0} · adjustments {1}',
    'strategy.goals': 'Goals: {0} on time · {1} stay · up to {2} deferred per shift',
    'strategy.signed': 'Proposed by {0} · validated by {1} · approved by {2} on {3}',
    'strategy.propose': 'Propose a profile',
    'strategy.editDraft': 'Edit draft',
    'strategy.proposeHelp': 'The analyst writes it, the planner says whether it can be run with the people there are, and command approves the approach. Three signatures, three people.',
    'strategy.name': 'Name',
    'strategy.division': 'Scope',
    'strategy.divisionAll': 'The whole agency',
    'strategy.divisionHelp': 'A division profile sits on top of the agency profile and may only change the stay factor (a multiple of the stay each hotspot already has), revisit period, lateness tolerance, maximum travel and the deferral cap. It carries no analytical method: how many hotspots are computed, and from how many incidents, sets the scale that risk is normalised against — the same scale used to compare one division with another.',
    'strategy.framework': 'Institutional approach',
    'strategy.rationale': 'Why',
    'strategy.rationaleHelp': 'What someone will read a year from now to understand why the agency patrols like this.',
    'strategy.analytics': 'Analytical method',
    'strategy.analyticsHelp': 'What ATLAS uses by default to calculate the hotspots this profile works with.',
    'strategy.timeWindow': 'Time window',
    'strategy.topHotspots': 'Hotspots per set',
    'strategy.minIncidents': 'Minimum incidents',
    'strategy.trainingDays': 'Days of history',
    'strategy.tacticTitle': 'Tactic',
    'strategy.tacticHelp': 'What TACTIX uses by default. The planner can still try something else in a scenario — that is what scenarios are for — but not by oversight.',
    'strategy.preset': 'Optimizer preset',
    'strategy.overrides': 'Adjustments (JSON)',
    'strategy.targets': 'Goals',
    'strategy.targetsHelp': 'What the agency expects. Without them, evaluating the profile is looking at numbers with no yardstick: 68% on time is neither good nor bad until somebody said what they aimed for.',
    'strategy.onTimeRate': 'On time (0–1)',
    'strategy.presenceRate': 'Stay (0–1)',
    'strategy.maxDeferred': 'Deferred per shift',
    'strategy.proposeAction': 'Propose',
    'strategy.save': 'Save draft',
    'strategy.cancel': 'Cancel',
    'strategy.all': 'Profiles · {0}',
    'strategy.noneYet': 'Nothing proposed yet.',
    'strategy.compareHint': 'Tick two to compare them.',
    'strategy.proposedBy': 'Proposed by {0} on {1}',
    'strategy.returned': 'Sent back: {0}',
    'strategy.approvedNote': 'When approving: {0}',
    'strategy.retiredNote': 'When retiring: {0}',
    'strategy.validatedNote': 'When validating: {0}',
    'strategy.scopeAgency': 'Whole agency',
    'strategy.scopeDivision': 'Division {0}, on top of the agency profile',
    'strategy.note': 'Note',
    'strategy.edit': 'Edit',
    'strategy.validate': 'Validate',
    'strategy.return': 'Send back',
    'strategy.approve': 'Approve',
    'strategy.retire': 'Retire',
    'strategyStatus.Draft': 'proposed · awaiting validation',
    'strategyStatus.Returned': 'sent back · the analyst corrects it',
    'strategyStatus.Validated': 'validated · awaiting approval',
    'strategyStatus.Active': 'in force',
    'strategyStatus.Retired': 'retired',
    'trends.byProfile': 'By strategy profile',
    'trends.byProfileHelp': 'How each profile fared. This is what closes the loop: command approves a profile, INSIGHT evaluates it, and that evaluation feeds the next version.',
    'trends.noProfile': 'no profile',
    'denial.event.id_missing': 'The event arrived without an identifier, so it cannot be recognised if it is sent again.',
    'denial.event.in_future': 'The event is dated in the future: check the clock on the phone.',
    'denial.event.needs_assignment': 'This event has to say which assignment it belongs to.',
    'denial.event.needs_position': 'This event has to carry a position.',
    'denial.event.bad_coordinates': 'The coordinates are out of range.',
    'denial.event.bad_accuracy': 'The position accuracy is not usable.',
    'denial.assignment.missing': 'That assignment does not exist.',
  'denial.token.incomplete': 'Your sign-in is incomplete. Sign in again.',
  'denial.device.unregistered': 'That phone is not registered with the agency.',
  'denial.device.revoked': 'That phone was revoked.',
  'denial.device.other_agency': 'That phone belongs to another agency.',
  'denial.agency.unknown': 'Unknown agency: {agency}.',
  'denial.agency.other_host': 'This account belongs to another agency than this address.',
  'signin.goToOwnHost': 'Go to {host}',
  'denial.duty.off_duty': 'That officer is not on a patrol unit for a shift in progress.',
  'denial.duty.shift_closed': 'Unit {callSign} already closed the shift.',
  'denial.duty.absent': 'HR published that officer as absent for this shift.',
  'denial.batch.too_large': 'Too many records at once: the server takes up to {max}.',
  'denial.event.too_old': 'The record is more than {hours} hours old.',
  'denial.unit.unknown': 'That unit no longer exists.',
  'denial.assignment.not_ours': 'That assignment belongs to another unit.',
  'denial.vehicle.mismatch': 'Vehicle {confirmed} is not the one assigned to {callSign} ({expected}).',
  'denial.state.invalid': 'That step is not allowed right now.',
  'denial.request.invalid': 'The server rejected the request.',
  'denial.not_found': 'It no longer exists on the server.',
  'denial.app.session_expired': 'Your session expired. Sign in again.',
  'denial.app.forbidden': 'Your role does not allow this.',
  'denial.app.unreachable': 'No connection to the server.',
  'denial.scenario.no_availability': 'No availability has been published for this shift: ask HR (officers) and Fleet (vehicles).',
  'denial.scenario.no_units': 'No units have been formed for this shift: form them under Units before generating.',
  'denial.ingest.not_configured': 'The server is missing the NIBRS taxonomy or the patrol tier policy, so it cannot bring in reports. Ask whoever runs the installation.',
  'denial.app.server_error': 'The server could not finish this. Try again, and if it happens again give support the reference.',
}

type Key = keyof typeof en

const es: Record<Key, string> = {
  'app.title': 'PROTECT Centro de Mando',
  'app.signIn': 'Ingresar',
  'app.server': 'Servidor',
  'app.agency': 'Agencia',
  'app.signOut': 'Cerrar sesión',
  'app.signInProvider': 'Ingresar con el proveedor de la agencia',
  'app.noProvider': 'Este despliegue todavía no tiene proveedor de identidad configurado.',
  'app.signedInAs': 'Sesión de {0}',
  'app.theme': 'Cambiar tema',
  'app.language': 'Cambiar idioma',
  'app.refresh': 'Actualizar',

  'districts.title': 'Límites de los distritos',
  'districts.help': 'Suba los límites que publica su ciudad, en GeoJSON. Con ellos, de qué división es un hotspot sale de dónde está, en vez del beat anotado en sus incidentes. Volver a subir reemplaza el conjunto completo.',
  'districts.file': 'Archivo GeoJSON',
  'districts.upload': 'Subir límites',
  'districts.uploading': 'Subiendo…',
  'districts.loaded': 'Se cargaron {0} de {1} límites.',
  'districts.none': 'Sin límites cargados. La división se resuelve por el prefijo del beat.',
  'geo.validFrom': 'Vigente desde (vacío: desde ahora)',
  'geo.validFromHelp': 'No se borra nada: la versión vigente se cierra y esta empieza en esa fecha. Una redistritación se puede cargar por adelantado. No puede empezar en el pasado.',
  'geo.version': 'Versión {0}, vigente desde {1}.',
  'geo.versionScheduled': 'La versión {0} queda programada: empieza el {1}. Hasta entonces sigue rigiendo la versión {2}.',
  'geo.versionScheduledFirst': 'La versión {0} queda programada: empieza el {1}. Hasta entonces no rige ninguna.',
  'geo.colVersion': 'Versión', 'geo.colFrom': 'Desde', 'geo.colTo': 'Hasta', 'geo.colAreas': 'Áreas', 'geo.colBy': 'Cargó',
  'geo.open': 'vigente', 'geo.scheduled': 'programada', 'geo.always': 'siempre',
  'geo.overridden': 'Atención: la carga programada para el {0} reemplazará a esta en esa fecha.',
  'districts.rejected': '{0} no se cargaron:',
  'districts.problem.missing_code': 'sin código',
  'districts.problem.not_an_area': 'no es un polígono',
  'districts.problem.duplicate': 'código repetido en el archivo',
  'districts.entry': 'entidad {0}',
  'denial.reference': 'ref. {0}',
  'zones.title': 'Zonas de patrullaje',
  'zones.help': 'Suba las formas que su corporación patrulla, en GeoJSON: un parque, un pasillo comercial, un tramo de avenida. Con ellas, «fuera de zona» se mide contra la forma y no contra un radio alrededor del hotspot. Volver a subir reemplaza el conjunto completo.',
  'zones.file': 'Archivo GeoJSON con las zonas',
  'zones.upload': 'Subir zonas',
  'zones.uploading': 'Subiendo…',
  'zones.loaded': 'Se cargaron {0} de {1} zonas.',
  'zones.none': 'Sin zonas cargadas. «Fuera de zona» se mide como un radio alrededor del hotspot.',
  'zones.rejected': '{0} no se cargaron:',
  'zones.entry': 'entidad {0}',

  'shifts.title': 'Turnos de esta agencia',
  'shifts.help': 'A qué hora empieza y termina cada turno, siempre en la hora de la agencia ({0}), aunque el resto de la pantalla esté en su hora: define el horario de la agencia. Guardar reemplaza la lista completa: lo que no esté aquí deja de existir. Los turnos ya registrados se siguen pudiendo leer aunque los quite de aquí.',
  'shifts.code': 'Código',
  'shifts.name': 'Nombre',
  'shifts.start': 'Empieza',
  'shifts.end': 'Termina',
  'shifts.hours': 'Duración',
  'shifts.duration': '{0} h',
  'shifts.overnight': 'termina al día siguiente',
  'shifts.add': 'Agregar turno',
  'shifts.remove': 'Quitar',
  'shifts.save': 'Guardar turnos',
  'shifts.saved': 'Guardado: ya es el horario de la agencia',
  'shifts.duplicate': 'Dos turnos comparten código. El código viaja en cada plan y cada unidad, así que tiene que ser único.',
  'shifts.incomplete': 'Cada turno necesita código, nombre y dos horas distintas.',

  'report.open': 'Reporte para imprimir',
  'report.print': 'Imprimir',
  'report.close': 'Volver',
  'report.title': 'Reporte de desempeño de turnos',
  'report.issued': 'Emitido el {0} por {1}.',
  'report.partials': '{0} de los {1} turnos del periodo son parciales: se aprobaron sabiendo que algo quedaba sin cubrir. Sus cifras están en los totales y los bajan.',
  'report.planned': 'Asignaciones planeadas',
  'report.released': 'Liberadas a una unidad',
  'report.completed': 'Completadas',
  'report.deferred': 'Quedaron diferidas',
  'report.incidents': 'Incidentes',
  'report.division': 'División',
  'report.footer': 'PROTECT — generado a partir de los registros de turno de esta agencia.',

  'import.officers': 'Importar plantilla',
  'import.vehicles': 'Importar flota',
  'import.help': 'Suba el archivo que exporta su sistema de personal o de flota, en .csv o .xlsx. Volver a subirlo actualiza lo que ya está en vez de duplicarlo.',
  'import.file': 'Archivo por subir',
  'import.upload': 'Subir',
  'import.uploading': 'Subiendo…',
  'import.written': 'Se cargaron {0} de {1} filas.',
  'import.absent': 'Vinieron {0} de los {1} ya registrados. Los otros {2} NO venían, y NO se dieron de baja. Revise si el export salió completo.',
  'import.rejected': '{0} {0|fila no se cargó|filas no se cargaron}. Las demás sí.',
  'import.clean': 'Se cargaron todas las filas.',
  'import.kept': 'El archivo no trae columna de {0}: quien ya estaba registrado conserva lo que tenía.',
  'import.field.certs': 'certificaciones', 'import.field.hr': 'expediente', 'import.field.equipment': 'equipo', 'import.field.active': 'estatus',
  'import.ignoredPersonal': 'Columnas con datos personales que no se guardaron: {0}. PROTECT no guarda datos personales.',
  'import.ignoredUnknown': 'Columnas que no se reconocieron y no se usaron: {0}. Si alguna trae algo que PROTECT necesita (placa, rango, división, ingreso, certificaciones), cambie su encabezado y vuelva a subir.',
  'import.line': 'Línea',
  'import.column': 'Columna',
  'import.problem': 'Problema',
  'import.more': 'Y {0} más.',
  'import.fixAndRetry': 'Corrija esas líneas y vuelva a subir el archivo: actualiza en vez de duplicar.',
  'import.problem.missing': 'Falta un dato obligatorio.',
  'import.problem.bad_date': 'No se pudo leer la fecha. Use AAAA-MM-DD.',
  'import.problem.bad_value': 'Ese valor no es de los que se aceptan.',
  'import.problem.bad_status': 'Estatus no reconocido, así que la fila no se cargó: un vehículo que quizá está en el taller no sale como disponible. Use disponible, taller o baja (available, maintenance, out_of_service).',
  'import.problem.duplicate': 'Ese número ya había aparecido antes en el archivo.',
  'import.problem.too_many_rows': 'El archivo tiene demasiadas filas. No se cargó nada.',
  'import.problem.unreadable': 'No se pudo leer el archivo. Revise que sea un .csv o .xlsx de verdad.',

  'state.loading': 'Cargando…',
  'state.error': 'No se pudo cargar. El aviso de arriba dice por qué.',
  'state.stale': 'Se muestran los últimos datos recibidos: la recarga más reciente falló.',

  'command.proposalWaiting': '{1} propuso la versión {0} del plan para el turno en curso: reemplaza a la vigente cuando usted la apruebe.',
  'command.reviewProposal': 'Revisarla en Planificación',
  'app.helpNoPlan': 'El turno {0} corre sin plan publicado: no hay contra qué vigilar hasta que el jefe de turno apruebe uno. Arriba dice qué falta y a quién le toca.',
  'app.help': 'El plan está corriendo. Aquí se vigila contra lo que hacen las unidades: atienda cada desvío, siga los incidentes y pida una reoptimización cuando el turno cambie.',
  'alerts.help': 'Donde el plan y la calle no coinciden. Elija una para verla en el mapa.',
  'incidents.help': 'Lo que las unidades reportaron desde la calle.',
  'replan.help': 'Pide a TACTIX replanificar lo que queda del turno en las divisiones afectadas.',
  'units.help': 'Quién está de servicio, dónde está y si el teléfono está reportando.',
  'map.noAlertPosition': 'Este desvío todavía no tiene lugar en el mapa: la unidad no ha reportado posición.',
  'map.broken': 'El mapa no pudo dibujar sus capas de datos',

  'demo.title': 'Demostración',
  'demo.help': 'Pone unidades a trabajar contra esta misma API, como si los oficiales llevaran su teléfono. Solo en un despliegue de demostración.',
  'demo.units': 'Unidades',
  'demo.pace': 'Segundos por tramo',
  'demo.deviate': 'Una de cada cuántas se desvía',
  'demo.deviateNone': 'Ninguna',
  'demo.start': 'Arrancar',
  'demo.stop': 'Detener',
  'demo.running': 'En marcha · {0} {0|unidad|unidades} · {1} {1|visita completada|visitas completadas}',
  'demo.deviating': 'Se desvían a propósito: {0}',
  'demo.idle': 'Detenida.',

  'units.title': 'Unidades',
  'units.none': 'Ninguna unidad de servicio.',
  'units.filter': 'Filtrar por indicativo o división',
  'units.lastSeen': 'Visto {0}',
  'units.noPosition': 'Sin posición todavía',
  'units.assignment': 'En asignación',
  'units.next': 'Va a',
  'units.interrupted': 'Visita en pausa',
  'units.reason': 'Motivo',
  'reassign.open': 'Reasignar a otra unidad',
  'reassign.to': 'A',
  'reassign.why': 'Justificación (obligatoria): queda en la historia del plan',
  'reassign.help': 'Publica una versión nueva del plan firmada por usted. Al oficial que la tenía se le avisa que se retiró; la otra unidad la recibe.',
  'reassign.confirm': 'Reasignar',
  'reassign.cancel': 'Cancelar',
  'reassign.done': '{0} se reasignó a {1} (versión {2} del plan).',
  'reassign.sent': '{0} quedó con {1}: su teléfono la recibe en segundos.',
  'reassign.working': 'Reasignando…',
  'cancelVisit.open': 'Cancelar esta visita',
  'cancelVisit.why': 'Motivo (obligatorio): queda en la historia del plan',
  'cancelVisit.help': 'Publica una versión nueva del plan firmada por usted. Nadie recibe la visita; al oficial que la tenía se le avisa que se retiró.',
  'cancelVisit.confirm': 'Cancelar la visita',
  'cancelVisit.working': 'Cancelando…',
  'cancelVisit.done': '{0} se le retiró a {1}: su teléfono se entera en segundos.',

  'alerts.title': 'Desvíos del plan',
  'alerts.none': 'Sin desvíos abiertos.',
  'alerts.showAll': 'Mostrar también las resueltas',
  'alerts.search': 'Buscar por unidad, tipo o lugar',
  'alerts.sort': 'Orden',
  'alerts.sort.severity': 'Lo más grave primero',
  'alerts.sort.newest': 'Lo más reciente primero',
  'alerts.sort.oldest': 'Lo más antiguo primero',
  'alerts.sort.unit': 'Por unidad',
  'alerts.onlyVisible': 'Solo lo que muestra el mapa',
  'alerts.visit': 'Visita {0} · planeada {1}',
  'alerts.samePlace': '{0} desvíos en este mismo punto',
  'alerts.samePlaceOnly': 'Ver solo estas',
  'alerts.nearby': '{0} a menos de {1}',
  'alerts.nearbyOnly': 'Ver solo estas',
  'alerts.nearbyClear': 'Ver todas otra vez',
  'alerts.showing': 'Mostrando {0} de {1}',
  'alerts.noneMatch': 'Nada coincide con lo que busca.',
  'units.noneMatch': 'Ninguna unidad coincide con ese filtro.',
  'log.noneMatch': 'Nada de la bitácora coincide con estos filtros.',
  'alerts.assignTo': 'Asignar a',
  'alerts.supportNote': 'Cómo se atendió (hace falta para resolver)',
  'alerts.replan': 'Reoptimizar el plan',
  'alerts.note': 'Nota',
  'alerts.action.Acknowledge': 'Reconocer',
  'alerts.action.Assign': 'Asignar',
  'alerts.action.Escalate': 'Escalar',
  'alerts.action.Resolve': 'Resolver',
  'alerts.action.Close': 'Cerrar',
  'alerts.assignedTo': 'Asignada a {0}',

  'incidents.replanOut': '{0} quedó fuera del plan mientras lo atiende. Pida una reoptimización para repartir su trabajo.',
  'incidents.replanReturn': '{0} volvió al servicio. Pida una reoptimización para incluirla de nuevo.',
  'incidents.replanAsk': 'Pedir reoptimización',
  'signin.authorized': 'Solo personal autorizado',
  'signin.help': 'Ingrese con la cuenta de su departamento. La contraseña y el segundo factor los pide el proveedor de identidad de su agencia; PROTECT nunca los ve.',
  'signin.sso': 'Ingresar con SSO',
  'map.legendIncident': 'Incidente',
  'incidents.showOnMap': 'Ver en el mapa',
  'incidents.noLocation': 'Sin ubicación: el teléfono no tenía posición al reportarlo.',
  'time.agency': 'Hora de la agencia · {0}',
  'time.mine': 'Mi hora · {0}',
  'time.toMine': 'Las horas están en la hora de la agencia. Pulse para verlas en la suya.',
  'time.toAgency': 'Las horas están en su hora. Pulse para volver a la de la agencia.',
  'signin.checking': 'Consultando cómo se ingresa aquí…',
  'signin.unreachable': 'El servidor de PROTECT no contesta. Se vuelve a intentar solo; si sigue así, revise la red.',
  'signin.secureAccess': 'Acceso operacional seguro',
  'signin.https': 'Conexión segura (HTTPS)',
  'signin.encrypted': 'Datos cifrados en reposo',
  'signin.theme': 'Cambiar tema',
  'signin.pillars': 'Inteligencia  |  Operaciones  |  Comunidades más seguras',
  'signin.tagline': 'Predictive Readiness, Operations, Tactics & Enforcement Command Technology',
  'signin.dataToday': 'Datos hoy.\nUn mañana más seguro.',
  'signin.verbs': 'Analizar\nAnticipar\nDesplegar\nProteger',
  'signin.copyright': '© {0} Intelligence for Defense & Security Inc. Todos los derechos reservados.',
  'signin.license': 'PROTECT™ es tecnología propietaria. Su uso se rige por el contrato de licencia aplicable.',
  'theme.title': 'Tema de la agencia',
  'theme.help': 'Cómo se presenta PROTECT ante su gente: en el ingreso de la web y del teléfono, en la página de la contraseña y en toda la aplicación (en oscuro, fondo, paneles, botones y títulos; en claro, botones y títulos). Los colores de estado (bien, aviso, peligro) no cambian. El diseño es de PROTECT; el nombre, el lema, los colores y el escudo son de su agencia.',
  'theme.name': 'Nombre de la agencia',
  'theme.nameHelp': 'Como la llama su gente. Vacío usa el nombre registrado.',
  'theme.motto': 'Lema (opcional)',
  'theme.mottoExample': 'Servicio · Integridad · Comunidad',
  'theme.demoNotice': 'Aviso de demostración',
  'theme.demoNoticeExample': 'Demostración: los datos no son reales',
  'theme.demoNoticeHelp': 'Solo para agencias de demostración: sale en todas las pantallas, en la web y en la tablet. Déjelo vacío en una agencia real.',
  'theme.presets': 'Temas',
  'theme.preset.protect': 'PROTECT',
  'theme.preset.navy': 'Azul marino',
  'theme.preset.sheriff': 'Verde y oro',
  'theme.preset.tan': 'Café y arena',
  'theme.preset.graphite': 'Grafito',
  'theme.preset.burgundy': 'Borgoña',
  'theme.primary': 'Color principal',
  'theme.primaryHelp': 'El fondo. Tiene que ser oscuro: el texto encima es blanco.',
  'theme.accent': 'Color de acento',
  'theme.accentHelp': 'Botones, rayas y la retícula. Tiene que ser claro: el texto del botón es oscuro.',
  'theme.problem.format': 'Los colores van como #RRGGBB.',
  'theme.problem.primaryLight': 'El color principal es muy claro: el texto blanco no se lee sobre él. Elija uno más oscuro.',
  'theme.problem.accentDark': 'El acento es muy oscuro: el texto del botón de ingreso no se lee sobre él. Elija uno más claro.',
  'theme.problem.accentBlends': 'El acento se confunde con el color principal. Elija uno que resalte más.',
  'theme.save': 'Guardar tema',
  'theme.discard': 'Descartar cambios',
  'theme.saved': 'Guardado: así lo verá su gente',
  'theme.badge': 'Escudo',
  'theme.badgeHelp': 'El escudo o logotipo de su agencia, en PNG, JPEG o WebP de hasta 512 KB, mejor con fondo transparente. No se acepta SVG: puede llevar código, y el escudo se muestra antes de que nadie ingrese.',
  'theme.badgeChoose': 'Elegir la imagen del escudo',
  'theme.badgeType': 'El escudo tiene que ser una imagen PNG, JPEG o WebP.',
  'theme.badgeSize': 'La imagen pesa {0} KB; el máximo es 512 KB.',
  'theme.badgeUpload': 'Cargar escudo',
  'theme.badgeRemove': 'Quitar escudo',
  'theme.badgeNone': 'Sin escudo cargado: se muestra uno neutro con las iniciales de la agencia.',
  'theme.preview': 'Vista previa',
  'uncovered.title': 'Quedan sin ninguna visita ({0}), por riesgo:',
  'uncovered.item': '#{0} {1} · {2} · riesgo {3}',
  'uncovered.all': 'Ver {0} más',
  'uncovered.fewer': 'Ver menos',
  'incidents.replanDone': 'Reoptimización pedida',
  'incidents.reasonOut': '{0} atiende un incidente: {1}',
  'incidents.reasonReturn': '{0} vuelve al servicio tras: {1}',
  'incidents.title': 'Incidentes',
  'incidents.none': 'Sin incidentes.',
  'incidents.action.Validate': 'Validar',
  'incidents.action.Activate': 'Activar',
  'incidents.action.Control': 'Controlado',
  'incidents.action.Complete': 'Completar',
  'incidents.action.Cancel': 'Cancelar',
  'incidents.cancelNote': 'Por qué se cancela (hace falta)',
  'denial.incident.cancel_needs_note': 'Para cancelar el incidente de {callSign} hay que anotar por qué.',

  'replan.title': 'Reoptimización',
  'replan.shiftDate': 'Fecha del turno',
  'replan.shiftCode': 'Turno',
  'replan.reason': 'Motivo',
  'replan.request': 'Pedir reoptimización',
  'replan.requested': 'Pedida. El resultado llega a esta pantalla.',
  'replan.result.Proposed': '{0}: revisión {1} propuesta — {2} {2|visita nueva|visitas nuevas}, {3} {3|retirada|retiradas}. La decide el jefe de turno (arriba).',
  'replan.result.NoChanges': '{0}: sin cambios. El plan vigente ya es el mejor con lo disponible.',
  'replan.result.Failed': '{0}: la reoptimización falló. Sigue vigente el plan actual; revise la bitácora.',

  'shift.running': 'Turno en curso: {0} {1}',
  'shift.remaining': 'quedan {0} h {1} min',
  'shift.alsoRunning': 'También corre {0}.',
  'shift.alsoRunningNoPlan': 'También corre {0}, sin plan publicado.',
  'shift.none': 'Ahora mismo no corre ningún turno, según las plantillas de la agencia.',

  'log.title': 'Bitácora del turno',
  'log.help': 'Qué ha pasado, lo más reciente primero. Corre de un turno a otro: el relevo es un renglón dentro de ella, no un corte.',
  'log.window': 'Hasta dónde atrás',
  'log.lastHours': 'Últimas {0} h',
  'log.filter': 'Mostrar',
  'log.all': 'Todo',
  'log.unit': 'Unidad',
  'log.showing': 'Se muestran {0} de {1}',
  'log.quiet': 'Nada en esta ventana. Un rato tranquilo es un rato tranquilo.',
  'log.by': 'lo hizo {0}',
  'log.detected': 'lo detectó el sistema',
  'log.family.alert': 'Desvíos',
  'log.family.incident': 'Incidentes',
  'log.family.unit': 'Unidades',
  'log.family.reoptimization': 'Reoptimización',
  'log.family.handover': 'Entrega',
  'log.family.plan': 'Plan',
  'log.alert.raised': 'Desvío detectado: {kind} ({severity})',
  'log.alert.acknowledge': 'Desvío reconocido',
  'log.alert.assign': 'Desvío asignado',
  'log.alert.escalate': 'Desvío escalado',
  'log.alert.resolve': 'Desvío resuelto',
  'log.alert.autoresolve': 'El desvío se corrigió solo',
  'log.alert.close': 'Desvío cerrado',
  'log.incident.reported': 'Incidente reportado desde la calle',
  'log.incident.validate': 'Incidente validado',
  'log.incident.activate': 'Incidente activo',
  'log.incident.control': 'Incidente bajo control',
  'log.incident.complete': 'Incidente terminado',
  'log.incident.cancel': 'Incidente cancelado',
  'log.unit.StartShift': 'Inició el turno',
  'log.unit.Acknowledge': 'Reconoció una asignación',
  'log.unit.CheckIn': 'Llegó al punto',
  'log.unit.CheckOut': 'Salió del punto',
  'log.unit.IncidentStarted': 'Salió a un incidente',
  'log.unit.IncidentCompleted': 'Liberó el incidente',
  'log.unit.EndShift': 'Terminó el turno',
  'log.unit.rejected': 'El teléfono intentó {kind} y se le negó ({code})',
  'log.unit.datalost': 'El teléfono borró {count} registros que no pudo enviar: {reason}',
  'command.tabNow': 'Desvíos',
  'command.tabNowBadge': 'Desvíos del plan nuevos',
  'command.tabIncidents': 'Incidentes',
  'command.tabIncidentsBadge': 'Incidentes abiertos',
  'command.tabUnits': 'Unidades',
  'command.tabUnitsBadge': 'Unidades de servicio sin conexión',
  'command.tabPlan': 'Plan',
  'command.tabPlanBadge': 'Una revisión espera decisión',
  'command.tabShift': 'Turno',
  'resources.search': 'Buscar por placa, rango o división',
  'resources.searchVehicle': 'Buscar por número, tipo o división',
  'resources.filterStatus': 'Mostrar',
  'resources.all': 'Todos · {0}',
  'resources.showing': 'Mostrando {0} de {1}',
  'resources.markShownAvailable': 'Marcar como disponibles los mostrados ({0})',
  'resources.markShownOut': 'Sacar del turno a los mostrados ({0})',
  'resources.noneMatch': 'Nada coincide con la búsqueda.',
  'admin.deadLetterReason': 'Qué se revisó y qué se hizo (hace falta para descartar)',
  'admin.deadLetterDiscard': 'Descartar',
  'ready.title': 'Preparar el turno {0} ({1})',
  'ready.step.officers': 'RH publica oficiales',
  'ready.step.vehicles': 'Flota publica vehículos',
  'ready.step.hotspots': 'El analista publica los hotspots del turno',
  'ready.step.strategy': 'El jefe aprueba la estrategia',
  'ready.step.units': 'El planificador arma unidades',
  'ready.step.proposal': 'El planificador propone el plan',
  'ready.step.approval': 'El jefe de turno aprueba y publica',
  'ready.who.officers': 'a RH', 'ready.who.vehicles': 'a Flota', 'ready.who.hotspots': 'al analista',
  'ready.who.strategy': 'al jefe', 'ready.who.units': 'al planificador', 'ready.who.proposal': 'al planificador',
  'ready.who.approval': 'al jefe de turno',
  'ready.yourTurn': 'Le toca a usted: {0}.',
  'ready.go': 'Ir',
  'ready.waiting': 'Lo siguiente le toca {0}.',
  'ready.returned': 'la versión {0} fue devuelta: vea por qué en el plan',
  'ready.allDone': 'El plan está publicado: el turno está listo.',
  'alerts.escalateNote': 'Qué hace falta (para escalar)',
  'log.alert.autoescalate': 'Nadie la reconoció a tiempo: se escaló al jefe de turno',
  'log.unit.SupportRequested': 'Pidió apoyo',
  'log.unit.UnitUnavailable': 'Reportó la unidad no disponible',
  'log.unit.UnitRestored': 'Reportó la unidad disponible de nuevo',
  'denial.alert.escalate_needs_note': 'Para escalar la alerta de {callSign} al jefe de turno hay que decir qué hace falta.',
  'units.m': 'metros',
  'units.ft': 'pies',
  'theme.units': 'Distancias en',
  'theme.unitsAuto': 'Según el país ({0})',
  'theme.units.imperial': 'pies y millas',
  'theme.units.metric': 'metros y kilómetros',
  'mfa.required': 'Segundo factor: exigido, comprobado en cada token de ingreso.',
  'mfa.provider': 'Segundo factor: se declaró que lo garantiza el proveedor de identidad; PROTECT no lo comprueba.',
  'mfa.off': 'Segundo factor: no se exige. CJIS lo exige para acceder a información criminal.',
  'denial.auth.mfa_required': 'Su agencia exige segundo factor y el proveedor de identidad no lo confirmó en este ingreso. Vuelva a ingresar usando su segundo factor.',
  'config.rulesByChief': 'Estas reglas las cambia el jefe (§7.1): aquí se leen, no se editan.',
  'config.layersByAnalyst': 'Los distritos y las zonas los carga el analista, desde Hotspots › Capas.',
  'analyst.tabLayers': 'Capas',
  'radio.incident': 'Incidente reportado por radio…',
  'radio.unavailable': 'Unidad no disponible…',
  'radio.restored': 'Disponible de nuevo',
  'radio.incidentWhat': 'Qué pasa (hace falta)',
  'radio.unavailableWhy': 'Por qué no está disponible (hace falta)',
  'radio.confirm': 'Registrar',
  'radio.help': 'Para lo que la unidad reportó por radio: tiene el mismo efecto que si llegara del teléfono, y la bitácora dice quién lo registró.',
  'radio.incidentDone': 'Incidente registrado para {0}. Si tenía una visita en curso quedó en pausa: pida una reoptimización para repartir el resto.',
  'radio.unavailableDone': '{0} ya no está disponible. Pida una reoptimización para repartir sus visitas.',
  'radio.restoredDone': '{0} está disponible de nuevo. Pida una reoptimización para darle trabajo.',
  'log.unit.unavailable': 'No disponible ({source})',
  'log.unit.restored': 'Disponible de nuevo ({source})',
  'source.radio': 'por radio',
  'source.phone': 'desde el teléfono',
  'denial.unit.unavailable_needs_reason': 'Para marcar a {callSign} como no disponible hay que decir por qué.',
  'denial.incident.needs_description': 'Para registrar el incidente de {callSign} hay que decir qué pasa.',
  'loss.offline_expired': 'demasiado tiempo sin conexión',
  'loss.signed_out': 'el oficial cerró sesión con registros pendientes',
  'log.reoptimization.proposed': 'La reoptimización propuso un cambio: {added} entran, {removed} salen, {units} unidades fuera de servicio',
  'log.reoptimization.nochanges': 'La reoptimización no encontró nada que cambiar',
  'log.reoptimization.failed': 'La reoptimización falló: sigue vigente el plan en curso',
  'log.reoptimization.returned': 'Revisión devuelta para correcciones',
  'log.plan.published': 'Plan {shift} versión {version} aprobado y publicado ({assignments} visitas; lo propuso {proposedBy})',
  'log.plan.reoptimization': 'Revisión {version} aprobada y publicada ({assignments} visitas; la pidió {proposedBy})',
  'log.plan.manual': 'Reasignación manual: versión {version} firmada',
  'log.handover.delivered': 'Se entregó el turno {shift}',
  'log.handover.received': 'Se recibió el turno {shift}',

  'handover.title': 'Entrega de turno',
  'handover.help': 'Lo que se le deja a quien entra, con constancia: qué queda abierto, qué tiene que saber, y las dos firmas.',
  'handover.wouldHandOver': 'Lo que entregaría',
  'handover.counts': 'Unidades todavía en la calle: {0} · Asignaciones abiertas: {1} · Alertas: {2} · Incidentes: {3}',
  'handover.critical': 'Alertas críticas: {0}',
  'handover.silent': 'Unidades en la calle sin reportar: {0}',
  'handover.stillOut': 'Siguen en la calle',
  'handover.clean': 'No queda nada abierto. Este turno se entrega limpio.',
  'handover.notes': 'Algo más que convenga saber (opcional)',
  'handover.notesRequired': 'Queda algo abierto: diga qué tiene que saber el turno que entra',
  'handover.deliver': 'Entregar el turno',
  'handover.deliveredBy': 'Entregó {0}',
  'handover.asDelivered': 'Como estaba al entregar',
  'handover.frozen': 'Es la foto del momento en que se firmó, no la situación de ahora.',
  'handover.receivedBy': 'Recibió {0}',
  'handover.receiptNote': 'Algo que anotar al recibir (opcional)',
  'handover.receive': 'Recibir el turno',
  'handover.waitingForOther': 'Falta que lo reciba quien entra: quien entrega no puede recibir.',
  'handoverStatus.Delivered': 'Entregado',
  'handoverStatus.Received': 'Recibido',
  'denial.handover.needs_note': 'Queda algo abierto, así que la entrega necesita una nota que diga qué tiene que saber el turno que entra.',
  'denial.alert.support_needs_note': 'Para resolver el pedido de apoyo de {callSign} hay que anotar cómo se atendió.',
  'denial.handover.same_person': 'Quien entrega no puede recibir: una entrega firmada por la misma persona de los dos lados no transfirió nada.',

  'map.units': 'Unidades',
  'map.assignments': 'Asignaciones',
  'map.legendUnit': 'Unidad',
  'map.legendTarget': 'Punto asignado',
  'map.legendAlert': 'Unidad con desvío abierto',
  'map.legendWarning': 'Desvío: aviso',
  'map.legendCritical': 'Desvío: crítico',
  'map.stack': '{0} desvíos aquí',

  'alert.LateArrival': '{callSign} no ha llegado a {label}: {minutes} min de retraso.',
  'alert.ShiftNotStarted': '{callSign} no inició el turno: {visits} visitas sin atender, la primera hace {minutes} min.',
  'alert.ShiftNotStarted.one': '{callSign} no inició el turno: 1 visita sin atender, desde hace {minutes} min.',
  'alert.ShortPresence': '{callSign} estuvo {actual} de {planned} min en {label}.',
  'alert.OutsideJurisdiction': '{callSign} está a {distance} fuera de la jurisdicción.',
  'alert.OutOfZone': '{callSign} está a {distance} de {label} durante la visita.',
  'alert.SupportRequested': '{callSign} pide apoyo. {reason}',
  'alert.IncidentSighted': '{callSign} avisa de un incidente que no puede atender: {reason}',
  'alert.UnitUnavailable': '{callSign} está fuera de servicio con {visits} visitas comunicadas: reoptimice el plan para repartirlas.',
  'alert.UnitUnavailable.one': '{callSign} está fuera de servicio con 1 visita comunicada: reoptimice el plan para reasignarla.',
  'alert.ConnectivityLost': 'Sin noticias de {callSign} hace {minutes} min.',

  'kind.LateArrival': 'Llegada tardía',
  'kind.ShiftNotStarted': 'No inició el turno',
  'kind.ShortPresence': 'Presencia corta',
  'kind.OutsideJurisdiction': 'Fuera de jurisdicción',
  'kind.OutOfZone': 'Fuera de zona',
  'kind.SupportRequested': 'Pide apoyo',
  'kind.IncidentSighted': 'Aviso de incidente',
  'kind.UnitUnavailable': 'Fuera de servicio',
  'kind.ConnectivityLost': 'Sin conexión',

  'severity.Warning': 'Aviso',
  'severity.Critical': 'Crítica',

  'status.New': 'Nueva',
  'status.Acknowledged': 'Reconocida',
  'status.Assigned': 'Asignada',
  'status.Escalated': 'Escalada',
  'status.Resolved': 'Resuelta',
  'status.Closed': 'Cerrada',
  'status.Reported': 'Reportado',
  'status.Validated': 'Validado',
  'status.Active': 'Activo',
  'status.Controlled': 'Controlado',
  'status.Completed': 'Completado',
  'status.Cancelled': 'Cancelado',

  'unit.OffDuty': 'Fuera de servicio',
  'unit.Available': 'Disponible',
  'unit.EnRoute': 'En camino',
  'unit.Patrolling': 'Patrullando',
  'unit.IncidentResponse': 'Atendiendo incidente',
  'unit.Returning': 'Regresando',
  'unit.Unavailable': 'No disponible',
  'unit.EndShift': 'Turno cerrado',

  'connectivity.Online': 'En línea',
  'connectivity.Stale': 'Atrasada',
  'connectivity.Offline': 'Sin conexión',
  'connectivity.Never': 'Nunca reportó',

  'assignment.Released': 'Comunicada',
  'assignment.Acknowledged': 'Recibida',
  'assignment.InProgress': 'En el punto',
  'assignment.Completed': 'Terminada',

    'license.evaluation': 'Sin licencia instalada: rigen los límites de evaluación.',
    'license.expired': 'La licencia venció el {0}. El turno sigue corriendo; no se puede planificar más.',
    'license.grace': 'La licencia venció el {0}. Por ahora todo sigue funcionando: conviene renovarla.',
    'license.invalid': 'No se pudo validar la licencia: rigen los límites de evaluación.',
    'license.expiring': 'La licencia vence el {0}, en {1} {1|día|días}.',
    'license.soldTo': 'Licencia de {0}',
    'denial.license.agency_not_covered': 'La agencia {agency} no está en la licencia de este despliegue.',
    'denial.license.units_exceeded': 'La licencia cubre {max} unidades por turno y el turno ya tiene {formed}.',
    'denial.license.module_not_covered': 'El módulo {module} no está contratado en esta licencia.',
    'denial.license.expired': 'La licencia venció el {expiredOn} y se agotó el periodo de gracia.',
    'nav.command': 'Centro de Mando',
    'nav.analyst': 'Hotspots',
    'nav.planner': 'Planificación',
    'analyst.help': 'Dónde está el delito. Calcule un conjunto, revíselo y publique el que debe usar el turno.',
    'analyst.calculate': 'Cálculo nuevo',
    'analyst.calculateHelp': 'La misma entrada produce el mismo conjunto: un cálculo sigue siendo auditable meses después.',
    'analyst.name': 'Nombre del conjunto',
    'analyst.from': 'Desde',
    'analyst.to': 'Hasta',
    'analyst.method': 'Método',
    'analyst.method.grid_count': 'Conteo por celda',
    'analyst.method.kde': 'Densidad kernel (KDE)',
    'analyst.window': 'Ventana horaria',
    'analyst.window.ALL_DAY': 'Todo el día',
    'analyst.top': 'Cuántos hotspots',
    'analyst.windowWarning': 'La ventana horaria importa: los hotspots de mañana y de noche coinciden apenas en una quinta parte. Calcule un conjunto por turno.',
    'analyst.run': 'Calcular',
    'analyst.sets': 'Conjuntos',
    'analyst.noSets': 'Todavía no hay conjuntos.',
    'analyst.incidents': 'incidentes',
    'analyst.createdBy': 'Calculado por {0}',
    'analyst.fewer': 'Pidió {0}: solo {1} lugares llegan al mínimo de {2} incidentes en esta ventana y periodo. Para más, amplíe el periodo.',
    'analyst.publishedBy': 'Publicado por {0}',
    'analyst.publish': 'Publicar',
    'analyst.hotspots': 'Hotspots',
    'analyst.fingerprint': 'Huella de entrada {0}…',
    'analyst.hotspotLine': '{0} {0|incidente|incidentes} · riesgo {1} · permanecer {2} min',
    'analyst.beat': 'Beat {0}',
    'setStatus.Draft': 'Borrador',
    'setStatus.Candidate': 'Candidato',
    'setStatus.Published': 'Publicado',
    'setStatus.Retired': 'Reemplazado',
    'priority.High': 'Alta',
    'priority.Medium': 'Media',
    'priority.Low': 'Baja',
    'denial.hotspots.forbidden': 'No tiene permiso para hacer eso.',
    'planner.help': 'Con qué cuenta el turno, qué propone el optimizador y cuál escenario se vuelve el plan.',
    'planner.shift': 'Turno',
    'planner.resources': 'Con qué cuenta el turno',
    'planner.officers': '{0} {0|oficial disponible|oficiales disponibles}',
    'planner.vehicles': '{0} {0|vehículo disponible|vehículos disponibles}',
    'planner.units': 'Unidades · {0}',
    'planner.noUnits': 'Todavía no hay unidades conformadas para este turno.',
    'planner.plan': 'Plan',
    'planner.noPlan': 'No hay plan publicado para este turno.',
    'planner.planLine': 'Versión {0} · {1} asignaciones · propuesta por {2}',
    'planner.approvedBy': 'Aprobada por {0}',
    'planner.pending': 'La versión {0} espera aprobación, propuesta por {1}',
    'planner.returned': '{1} devolvió la versión {0} para corregir:',
    'planner.acceptPartial': 'Acepto que es parcial: {0} hotspots quedan sin ninguna visita',
    'revision.title': 'Revisión {0} en espera de aprobación',
    'revision.requested': 'La pidió {0} a las {1}: {2}',
    'revision.coverage': 'Cobertura {0} % → {1} %',
    'revision.kept': '{0} sin tocar (hechas, en curso, ya comunicadas, por salir en minutos o de divisiones que no se replanifican) · {1} planeadas que se conservan igual',
    'revision.excluded': 'Quedan fuera: {0}',
    'revision.notStarted': 'No iniciaron el turno ({0}), quedan fuera: {1}',
    'revision.andMore': '{0} y {1} más',
    'revision.cancelDisclosed': 'Se retiran a oficiales que ya las tenían ({0}):',
    'revision.reassign': 'Visitas interrumpidas que se cierran; su hotspot vuelve al reparto si queda tiempo ({0}):',
    'revision.removed': 'Visitas planeadas que se cancelan ({0}):',
    'revision.added': 'Visitas nuevas ({0}):',
    'revision.acceptPartial': 'Acepto que el plan queda parcial: hay hotspots sin ninguna visita',
    'revision.stale': 'Esta revisión ya no corresponde al plan y no se puede publicar: {0}',
    'revision.sameAsReturned': 'Es la misma propuesta que la revisión {0}, que {1} devolvió: «{2}». La corrida no cambió nada de lo objetado. Si la devuelve otra vez, diga qué debe hacer distinto el motor (unidades, alcance, ventana).',
    'revision.requestAgain': 'Pedir una nueva con el mismo motivo',
    'revision.otherApprover': 'Usted pidió esta revisión y la agencia exige que la apruebe otra persona (cuatro ojos). Sí puede devolverla.',
    'revision.waiting': 'La decide el jefe de turno: al aprobarla se publica y los teléfonos reciben lo suyo.',
    'note.byManual': 'reemplazada por el ajuste manual {0}',
    'note.byReoptimization': 'reemplazada por la reoptimización {0}',
    'note.byVersion': 'reemplazada por la versión {0}',
    'note.byLaterProposal': 'reemplazada por una propuesta posterior',
    'note.byLaterReoptimization': 'reemplazada por una reoptimización posterior',
    'planner.approve': 'Aprobar y publicar',
    'planner.reject': 'Devolver para corregir',
    'planner.rejectReason': 'Qué hay que cambiar',
    'planner.propose': 'Proponer este escenario',
    'planner.proposedAs': 'Propuesto como versión {0}: espera al jefe de turno',
    'planner.scenarios': 'Escenarios · {0}',
    'planner.noScenarios': 'Todavía no hay escenarios para este turno.',
    'planner.generate': 'Escenario nuevo',
    'planner.preset': 'Punto de partida',
    'planner.fromProfile': 'como el perfil',
    'planner.fromProfileHelp': 'Lo que deje «como el perfil» es lo que aprobó el mando. Cambie solo lo que quiera probar.',
    'planner.preset.base': 'Base, sin mejoras',
    'planner.preset.protect-base': 'Base de PROTECT',
    'planner.rule': 'Regla de división',
    'planner.rule.prefer': 'Preferir la propia división',
    'planner.rule.strict': 'Solo la propia división',
    'planner.rule.ignore': 'Ignorar divisiones',
    'planner.divisionKnobs': '{0}: revisita {1} min, permanencia ×{2}',
    'planner.revisit': 'Revisita (min)',
    'planner.hourly': 'Riesgo por hora',
    'planner.lookahead': 'Anticipación',
    'planner.reserve': 'Reserva de respuesta',
    'planner.run': 'Generar',
    'planner.crewSize': '{0} {0|oficial|oficiales}',
    'planner.needsAvailability': 'No hay disponibilidad publicada para este turno: pídasela a RH (oficiales) y a Flota (vehículos). Sin ella no hay unidades que armar.',
    'planner.needsUnits': 'Todavía no hay unidades conformadas para este turno: ármelas en la pestaña Unidades y después genere.',
    'planner.kpis': 'Cobertura {0} % · ponderada {1} % · {2}/{3} visitas · uso {4} %',
    'planner.knobs': 'Riesgo horario {0} · anticipación {1} · reserva {2} · revisita {3} min',
    'planner.on': 'sí',
    'planner.off': 'no',
    'planner.partial': 'Parcial: {0} hotspots sin ninguna visita',
    'planner.crossDivision': '{0} {0|visita|visitas} fuera de la división',
    'planner.tip': 'Genere dos y compare: los números son en lo que se apoya la decisión.',
    'assignment.Planned': 'Planeada',
    'assignment.Interrupted': 'Interrumpida',
    'assignment.Missed': 'No atendida',
    'assignment.Reassigned': 'Reasignada',
    'assignment.Cancelled': 'Cancelada',
    'crew.title': 'Conformar una unidad',
    'crew.help': 'Elija la tripulación y el vehículo. Las reglas se revisan mientras arma, antes de guardar nada.',
    'crew.type': 'Tipo de unidad',
    'crew.callSign': 'Indicativo',
    'crew.division': 'División',
    'crew.officers': 'Tripulación · {0} de {1}',
    'crew.vehicle': 'Vehículo',
    'crew.pickVehicle': 'Elija un vehículo',
    'crew.taken': 'en {0}',
    'crew.years': '{0} años',
    'crew.clean': 'No se incumple ninguna regla.',
    'crew.blocked': 'Esta unidad no se puede conformar, con autorización o sin ella.',
    'crew.needsException': 'Necesita una excepción autorizada.',
    'crew.exceptionReason': 'Por qué se autoriza',
    'crew.cannotExcept': 'Solo la autoridad de turno puede firmar una excepción. Pídasela al comandante.',
    'crew.form': 'Conformar la unidad',
    'crew.formed': 'Unidad {0} conformada.',
    'severity.Soft': 'Advertencia',
    'severity.Hard': 'Requiere excepción',
    'severity.Blocking': 'No se permite',
    'rule.CREW_SIZE': 'Tamaño de la tripulación',
    'rule.OFFICER_INACTIVE': 'Oficial no activo',
    'rule.OFFICER_NOT_AVAILABLE': 'Oficial no disponible',
    'rule.OFFICER_DOUBLE_BOOKED': 'El oficial ya está en otra unidad',
    'rule.VEHICLE_NOT_AVAILABLE': 'Vehículo no disponible',
    'rule.VEHICLE_DOUBLE_BOOKED': 'El vehículo ya está en otra unidad',
    'rule.OFFICER_RESTRICTED': 'Oficial con servicio restringido',
    'rule.VEHICLE_TYPE_MISMATCH': 'Tipo de vehículo equivocado',
    'rule.ALL_ROOKIE_CREW': 'Tripulación solo de novatos',
    'rule.MISSING_CERTIFICATION': 'Falta una certificación',
    'rule.OUTSIDE_HOME_DIVISION': 'Fuera de su división',
    'rule.VEHICLE_OUTSIDE_BASE_DIVISION': 'Vehículo fuera de su división base',
    'ruleDetail.CREW_SIZE': 'El tipo {type} lleva entre {min} y {max} oficiales; se propusieron {count}.',
    'ruleDetail.OFFICER_INACTIVE': 'El oficial {badge} no está activo.',
    'ruleDetail.OFFICER_NOT_AVAILABLE.unpublished': 'No hay disponibilidad publicada del oficial {badge} para {shift}.',
    'ruleDetail.OFFICER_NOT_AVAILABLE.absent': 'El oficial {badge} está ausente en {shift}.',
    'ruleDetail.OFFICER_RESTRICTED': 'El oficial {badge} tiene servicio restringido en {shift}.',
    'ruleDetail.OFFICER_DOUBLE_BOOKED': 'El oficial {badge} ya integra la unidad {unit} en {shift}.',
    'ruleDetail.OUTSIDE_HOME_DIVISION': 'El oficial {badge} está adscrito a {home}, no a {division}.',
    'ruleDetail.ALL_ROOKIE_CREW': 'Todos los integrantes tienen menos de {years} año(s) de servicio.',
    'ruleDetail.MISSING_CERTIFICATION': 'Ningún integrante tiene la certificación {certification}.',
    'ruleDetail.VEHICLE_NOT_AVAILABLE.status': 'El vehículo {vehicle} no se puede usar: {status}.',
    'ruleDetail.VEHICLE_NOT_AVAILABLE.absent': 'El vehículo {vehicle} no está disponible en {shift}.',
    'ruleDetail.VEHICLE_DOUBLE_BOOKED': 'El vehículo {vehicle} ya está asignado a {unit} en {shift}.',
    'ruleDetail.VEHICLE_TYPE_MISMATCH': 'El tipo {type} lleva un vehículo {required}; {vehicle} es {actual}.',
    'ruleDetail.VEHICLE_OUTSIDE_BASE_DIVISION': 'El vehículo {vehicle} tiene base en {base}, no en {division}.',
    'denial.formation.exception_not_authorized': 'Solo la autoridad de turno puede firmar una excepción ({rules}).',
    'crew.pickVehicleFirst': 'Elija un vehículo para revisar las reglas.',
    'crew.needCallSign': 'Escriba el indicativo de la unidad para conformarla.',
    'reason.LEAVE': 'permiso',
    'reason.TRAINING': 'capacitación',
    'reason.LIGHT_DUTY': 'servicio ligero',
    'reason.MAINTENANCE': 'mantenimiento',
    'crew.cannotCheck': 'Ahora mismo no se pudieron revisar las reglas. Vuelva a intentar antes de conformar.',
    'vehicle.Available': 'Disponible',
    'vehicle.Assigned': 'Asignado',
    'vehicle.Maintenance': 'En taller',
    'rule.shift': 'el turno {0} del {1}',
    'vehicle.OutOfService': 'Fuera de servicio',
    'compare.title': 'Comparar',
    'compare.pick': 'Comparar',
    'compare.hint': 'Marque dos escenarios para compararlos.',
    'compare.changed': 'Qué cambia',
    'compare.result': 'Qué resultó',
    'compare.objective': 'lo que el optimizador maximiza',
    'compare.same': 'Misma configuración en los dos: la diferencia está en la corrida, no en las perillas.',
    'compare.wins': 'mejor',
    'kpi.coverage': 'Cobertura',
    'kpi.riskWeightedCoverage': 'Cobertura ponderada por riesgo',
    'kpi.visitRating': 'Calificación de visitas',
    'kpi.utilization': 'Uso de las unidades',
    'kpi.visits': 'Visitas',
    'kpi.expectedVisits': 'Visitas esperadas',
    'kpi.hotspotsNotServed': 'Hotspots sin visita',
    'kpi.crossDivisionVisits': 'Visitas fuera de la división',
    'knob.revisitMinutes': 'Periodo de revisita (min)',
    'knob.toleranceMinutes': 'Tolerancia de retraso (min)',
    'knob.defaultDwellMinutes': 'Permanencia por omisión (min)',
    'knob.dwellFactor': 'Factor de permanencia (× recomendada)',
    'knob.alpha': 'Riesgo contra equidad (alfa)',
    'knob.gamma': 'Escala del retraso (gamma)',
    'knob.divisionRule': 'Regla de división',
    'knob.maxTravelMinutes': 'Traslado máximo (min)',
    'knob.roadFactor': 'Factor de camino',
    'knob.useHourlyRisk': 'Riesgo por hora',
    'knob.useLookahead': 'Anticipación',
    'knob.useResponseReserve': 'Reserva de respuesta',
    'knob.reserveShare': 'Fracción de reserva',
    'knob.lookaheadMinutes': 'Anticipación (min)',
    'knob.minReservePerDivision': 'Reserva mínima por división',
    'nav.fleet': 'Flota',
    'nav.hr': 'Personal',
    'fleet.help': 'El catálogo de vehículos y con cuáles puede contar el turno.',
    'fleet.catalogue': 'Vehículos · {0}',
    'fleet.statusHelp': 'Mandar un vehículo al taller no lo saca de las unidades que ya lo tienen: eso es una reoptimización y la decide el mando.',
    'fleet.history': 'Historial',
    'fleet.closeHistory': 'Cerrar historial',
    'fleet.historyTitle': 'Dónde ha andado {0}',
    'fleet.neverOut': 'Este vehículo todavía no ha salido en ninguna unidad.',
    'fleet.historySummary': '{0} {0|turno trabajado|turnos trabajados} · {1} de {2} asignaciones completadas',
    'fleet.historyRange': 'Del {0} al {1}',
    'fleet.shift': 'Turno',
    'fleet.unit': 'Unidad',
    'fleet.crew': 'Tripulación',
    'fleet.work': 'Hechas',
    'fleet.status': 'Estado del vehículo',
    'hr.help': 'Con quién puede contar el turno, y el perfil de planeación de cada oficial.',
    'hr.roster': 'Oficiales · {0}',
    'hr.rosterHelp': 'Solo el perfil de planeación: placa, rango, división, antigüedad y certificaciones. El dato personal se queda en RR. HH.',
    'hr.active': 'Activo',
    'resources.availability': 'Disponibilidad del turno',
    'resources.replaces': 'Publicar reemplaza la lista completa: ahora hay {0} en el turno. Quien quede fuera deja de estar disponible.',
    'resources.notInShift': 'No está en este turno',
    'resources.status': 'Disponibilidad',
    'resources.reason': 'Motivo',
    'resources.publish': 'Publicar disponibilidad',
    'resources.nothingToPublish': 'Sin cambios desde que se publicó.',
    'resources.published': 'Publicado: {0} en el turno. Planificación ya lo ve.',
    'resources.needsReason': 'Una ausencia necesita motivo: quien planifica no puede cubrir lo que no tiene explicación.',
    'availability.Available': 'Disponible',
    'availability.Absent': 'Ausente',
    'availability.Restricted': 'Servicio restringido',
    'availability.vehicle.Available': 'Disponible',
    'availability.vehicle.Absent': 'No disponible (taller u otro)',
    'availability.vehicle.Restricted': 'Uso limitado',
    'resources.needsReasonVehicle': 'Un vehículo que no sale necesita motivo: quien planifica tiene que saber por qué falta.',
    'nav.admin': 'Administración',
    'admin.help': 'Los teléfonos con los que opera la agencia, y el registro de quién autorizó qué.',
    'admin.deployment': 'Este despliegue',
    'admin.provider': 'Proveedor de identidad: {0}',
    'admin.noProvider': 'Todavía no hay proveedor de identidad configurado.',
    'admin.demo': 'Modo de demostración: hay tokens de desarrollo y simulador.',
    'admin.license': 'Licencia: {0} · {1} · hasta {2}',
    'admin.deadLetters': '{0} mensajes nunca se procesaron ({1}), el último el {2}: alguien cree que ese trabajo se hizo. Revise el registro del servidor.',
    'admin.deadLetters.one': 'Un mensaje nunca se procesó ({1}), el {2}: alguien cree que ese trabajo se hizo. Revise el registro del servidor.',
    'admin.usersElsewhere': 'Los usuarios y los roles no se administran aquí: viven en el proveedor de identidad de la agencia, así que un oficial que deja el cuerpo pierde el acceso cuando su departamento lo da de baja, sin una segunda lista que alguien tenga que acordarse de cerrar.',
    'admin.devices': 'Teléfonos · {0}',
    'admin.devicesHelp': 'Un ingreso válido en un teléfono no registrado no entra. Revocarlo lo deja fuera desde la siguiente llamada.',
    'admin.deviceId': 'Identificador del teléfono',
    'admin.deviceLabel': 'Etiqueta (quién lo lleva)',
    'admin.register': 'Registrar',
    'admin.registeredAt': 'Registrado el {0}',
    'admin.revokedAt': 'Revocado el {0} · {1}',
    'admin.revokeReason': 'Por qué se revoca',
    'admin.revoke': 'Revocar',
    'admin.audit': 'Bitácora',
    'admin.auditHelp': 'Una sola línea de tiempo, de lo más reciente a lo más viejo. Es lo que responde, dentro de dos años, por qué se autorizó esa excepción.',
    'admin.auditEmpty': 'Todavía no hay nada registrado.',
    'admin.by': 'por {0}',
    'deviceStatus.Active': 'Activo',
    'deviceStatus.Revoked': 'Revocado',
    'licenseState.Evaluation': 'evaluación',
    'licenseState.Active': 'vigente',
    'licenseState.Grace': 'en periodo de gracia',
    'licenseState.Expired': 'vencida',
    'licenseState.Invalid': 'no válida',
    'audit.formation.exception': 'Excepción de conformación',
    'audit.plan.proposed': 'Plan propuesto',
    'audit.plan.published': 'Plan aprobado y publicado',
    'audit.plan.rejected': 'Plan rechazado',
    'audit.plan.superseded': 'Plan reemplazado',
    'audit.device.registered': 'Teléfono registrado',
    'audit.device.revoked': 'Teléfono revocado',
    'audit.device.reregistered': 'Teléfono registrado de nuevo',
    'admin.auditFilter': 'Filtro',
    'admin.auditAll': 'Todo',
    'admin.auditExceptions': 'Excepciones de conformación',
    'admin.auditPlans': 'Planes',
    'admin.auditDevices': 'Teléfonos',
    'ingest.title': 'Reportes',
    'ingest.help': 'La entrada de los reportes del departamento. Tarda minutos y corre en segundo plano: puede irse de la pantalla.',
    'ingest.source': 'Fuente',
    'ingest.run': 'Traer reportes',
    'ingest.running': 'Hay una ingesta en curso. El historial se actualiza solo; puede irse de la pantalla.',
    'ingest.window': 'Descargue una ventana más amplia que la que va a analizar: un incidente denunciado tarde queda fuera de una ventana ajustada.',
    'ingest.none': 'Todavía no se ha traído nada.',
    'ingest.line': '{0} filas escritas de {1} leídas · {2} descartadas',
    'ingest.skipped': 'Las filas descartadas no traen identificador de origen: sin llave natural, volver a traerlas las duplicaría.',
    'ingestStatus.RUNNING': 'En curso',
    'ingestStatus.COMPLETED': 'Terminada',
    'ingestStatus.FAILED': 'Falló',
    'ingestStatus.INTERRUPTED': 'Interrumpida',
    'ingest.interrupted': 'El servidor se reinició mientras corría. Lo escrito es válido: vuelva a traer el mismo periodo para completarlo.',
    'demo.formShift': 'Conformar las unidades del turno',
    'demo.formed': '{0} {0|unidad conformada|unidades conformadas}.',
    'demo.formedWithSkips': '{0} unidades conformadas, {1} no se pudieron: {2}',
    'coverage.title': '¿Alcanza el turno?',
    'coverage.help': 'Por división, porque cien oficiales de sobra en una no cubren un hueco en otra.',
    'coverage.none': 'La agencia no tiene dotación objetivo cargada, o no hay disponibilidad publicada para este turno.',
    'coverage.division': 'División',
    'coverage.officers': 'Oficiales',
    'coverage.sergeants': 'Sargentos',
    'coverage.vehicles': 'Vehículos',
    'coverage.formed': 'Conformadas',
    'coverage.ok': 'alcanza',
    'coverage.shortOfficers': '{0|falta|faltan} {0} {0|oficial|oficiales}',
    'coverage.shortSergeants': '{0|falta|faltan} {0} {0|sargento|sargentos}',
    'coverage.shortVehicles': 'faltan {0} vehículos',
    'coverage.aside': '{0} restringidos · {1} ausentes',
    'config.title': 'Configuración de la agencia',
    'config.help': 'Cómo está organizada esta agencia. No es dato del algoritmo ni del turno, y solo ella lo sabe.',
    'config.staffing': 'Dotación objetivo',
    'config.staffingHelp': 'Cuántos puestos y unidades pone cada división en cada turno. Es lo que contesta «¿alcanza el turno?».',
    'config.sectors': 'Sectores y divisiones',
    'config.sectorsHelp': 'Qué división responde por cada prefijo de beat. Sin esto el optimizador no sabe de quién es cada hotspot.',
    'config.division': 'División',
    'config.shift': 'Turno',
    'config.positions': 'Puestos',
    'config.oneOfficer': 'Unidades de 1',
    'config.twoOfficer': 'Unidades de 2',
    'config.sergeantUnits': 'Sargentos',
    'config.sector': 'Sector',
    'config.add': 'Agregar una fila',
    'config.remove': 'Quitar',
    'config.save': 'Guardar',
    'config.replaces': 'Guardar reemplaza la tabla completa: una fila que quite deja de existir.',
    'policy.title': 'Reglas de la agencia',
    'policy.help': 'Cuánto tolera esta agencia y quién firma un plan. Cada cambio queda en bitácora con su motivo.',
    'policy.deviation': 'Umbrales de desvío',
    'policy.deviationHelp': 'Cuándo se marca a una unidad fuera de plan. Rigen desde el turno siguiente, nunca a mitad de uno.',
    'policy.late': 'Retraso tolerado (minutos)',
    'policy.presence': 'Permanencia mínima (fracción de lo planeado)',
    'policy.geofence': 'Geocerca ({0})',
    'policy.current': 'Rige ahora, también en el turno en curso: {0}.',
    'policy.effective': 'Un cambio guardado ahora empieza a regir el {0}.',
    'policyLog.rookie': 'novato con menos de {0} {0|año|años}',
    'policyLog.certs': '{0} exige {1}',
    'policyLog.approval.none': 'sin aprobación',
    'policyLog.approval.any': 'aprobación de cualquiera con autoridad',
    'policyLog.approval.four_eyes': 'aprobación de alguien distinto de quien propone (cuatro ojos)',
    'policy.pending': 'Ya hay uno fijado para el {0}: {1}.',
    'policy.approval': 'Aprobación del plan',
    'policy.approvalHelp': 'Quién puede publicar un plan. Aflojarlo es una decisión de autoridad, y como tal se anota.',
    'policy.formation': 'Conformación de tripulaciones',
    'policy.formationHelp': 'Qué tolera esta agencia al armar una tripulación: una advertencia queda registrada, una excepción la firma el jefe de turno y lo que no se permite no se permite.',
    'policy.rookie': 'Novato por debajo de (años de servicio)',
    'policy.blockingAlways': 'Oficiales o vehículos ausentes, inactivos o ya asignados nunca se permiten: ninguna configuración cambia eso.',
    'policy.certsFor': 'Certificaciones exigidas en {0}',
    'policy.formationEffective': 'Rige desde la siguiente unidad que se arme. Las ya armadas conservan la versión con que se armaron.',
    'policy.requires': 'El plan necesita aprobación antes de publicarse',
    'policy.different': 'Quien aprueba no puede ser quien propuso (cuatro ojos)',
    'policy.differentHelp': 'Quítelo solo donde el que planifica es el que manda: si no, nadie puede publicar.',
    'policy.reason': 'Motivo',
    'policy.reasonHelp': 'Es lo que explica, meses después, por qué ese turno se midió distinto.',
    'policy.reasonHelpApproval': 'Es lo que explica, meses después, quién podía firmar un plan y por qué.',
    'policy.reasonHelpFormation': 'Es lo que explica, meses después, por qué se aceptó una tripulación o pidió excepción.',
    'policy.reasonNeeded': 'Escriba el motivo antes de guardar.',
    'policy.history': 'Bitácora de cambios',
    'policy.noHistory': 'No se ha cambiado nada: rigen las reglas de fábrica.',
    'policy.when': 'Cuándo',
    'policy.who': 'Quién',
    'policy.what': 'Qué quedó',
    'policy.why': 'Por qué',
    'admin.auditPolicies': 'Cambios de reglas',
    'admin.auditBranding': 'Tema',
    'admin.auditRoster': 'Importaciones de personal y flota',
    'audit.branding.changed': 'Cambio del tema de la agencia',
    'audit.roster.officer.imported': 'Personal importado',
    'audit.roster.vehicle.imported': 'Flota importada',
    'audit.policy.deviation': 'Cambio de umbrales',
    'audit.policy.approval': 'Cambio de la regla de aprobación',
    'audit.approval.none': 'El planificador publica sin aprobación',
    'audit.approval.four_eyes': 'Aprueba otra persona (cuatro ojos)',
    'audit.approval.any': 'Aprueba cualquiera, incluso quien propuso',
    'app.noAreas': 'Este ingreso no abre ninguna pantalla del Centro de Mando. Los roles que trae trabajan en otro lado: el oficial, por ejemplo, trabaja desde el teléfono.',
    'role.chief': 'Jefatura',
    'role.watch-commander': 'Jefe de turno',
    'role.command': 'Centro de mando',
    'role.planner': 'Planificación',
    'role.analyst': 'Análisis',
    'role.fleet': 'Flota',
    'role.hr': 'Recursos humanos',
    'role.admin': 'Administración',
    'storage.undeclared': 'Cifrado en reposo: sin declarar. Se trata como si no hubiera; declárelo después de cifrar el volumen.',
    'storage.none': 'Cifrado en reposo: ninguno. Los datos del turno, la ubicación de cada oficial y el histórico de incidentes quedan legibles para quien alcance el disco o un respaldo.',
    'storage.volume': 'Cifrado en reposo: volumen cifrado.',
    'storage.managed': 'Cifrado en reposo: servicio administrado.',
    'quality.title': 'Calidad de datos',
    'quality.help': 'Un incidente con defectos no se descarta al ingerirlo: se conserva marcado, para poder listarlo y explicarlo aquí. Lo que se descarta se vuelve invisible, y lo que no se ve no se arregla.',
    'quality.empty': 'Todavía no hay incidentes cargados. Importe una fuente primero.',
    'quality.usable': '{0} incidentes pueden alimentar un cálculo ({1} de lo cargado).',
    'quality.breakdown': '{0} limpios · {1} usables con reservas · {2} no usables',
    'quality.defects': 'Defectos',
    'quality.defect': 'Defecto',
    'quality.count': 'Cuántos',
    'quality.defectsHelp': 'Un incidente puede traer más de uno, así que los números no suman el total.',
    'quality.sources': 'Fuentes',
    'quality.sourcesHelp': 'Qué trajo cada una y hasta cuándo. La última fecha contesta si la fuente sigue viva.',
    'quality.source': 'Fuente',
    'quality.unusableShort': 'No usables',
    'quality.from': 'Desde',
    'quality.until': 'Hasta',
    'quality.samples': 'Filas para mirar',
    'quality.samplesHelp': 'Nadie corrige un porcentaje: se corrige la fila que se ve. Lo peor primero.',
    'qualityStatus.Ok': 'limpio',
    'qualityStatus.Review': 'con reservas',
    'qualityStatus.Unusable': 'no usable',
    'skip.no_external_id': 'sin identificador de origen',
    'skip.unreadable': 'no se pudo leer',
    'ingest.why': 'De las omitidas:',
    'ingest.flags': 'De las escritas, con algo faltante:',
    'ingest.clean': 'Todo lo escrito entró completo.',

    'flag.MissingGeo': 'sin coordenadas',
    'flag.OutOfBounds': 'fuera del área de la agencia',
    'flag.MissingOccurredAt': 'sin fecha de ocurrencia',
    'flag.ApproximateTime': 'hora rellenada por la fuente',
    'flag.NonNibrsCode': 'delito fuera de NIBRS',
    'flag.LowGeocodeScore': 'geocodificación floja',
    'explorer.title': 'Explorador de incidentes',
    'explorer.help': 'Lo que hay debajo de un hotspot. Los resultados se pintan en el mapa, en pequeño y por debajo: la pregunta es si el punto tiene detrás lo que dice tener.',
    'explorer.from': 'Desde',
    'explorer.to': 'Hasta',
    'explorer.tier': 'Nivel de patrullaje',
    'explorer.quality': 'Calidad',
    'explorer.any': 'Cualquiera',
    'explorer.search': 'Buscar',
    'explorer.window': 'Franja del día (hora de la agencia)',
    'explorer.allDay': 'Todo el día',
    'explorer.windowNeedsPeriod': 'Para mirar una franja del día, elija también desde y hasta.',
    'explorer.found': '{0} incidentes.',
    'explorer.truncated': 'Se muestran {0} de {1}. Acote el periodo para ver el resto.',
    'explorer.listCut': 'La lista muestra los 40 más recientes de {0}; el mapa los muestra todos.',
    'explorer.inexactHelp': 'La fuente rellenó la hora: no cuenta para el análisis por franja horaria.',
    'tier.StreetCrime': 'Delito callejero',
    'tier.PublicOrder': 'Orden público',
    'tier.IndoorDomestic': 'Violencia familiar',
    'tier.Excluded': 'No disuadible por presencia',
    'analyst.tabData': 'Datos',
    'analyst.tabIncidents': 'Incidentes',
    'analyst.tabHotspots': 'Hotspots',
    'changes.title': 'Qué cambió · v{0} → v{1} de {2}',
    'changes.differentGrid': 'Las dos corridas usaron retículas distintas, así que los puntos se emparejan por cercanía dentro de {0} m. Un punto que solo se corrió de celda puede aparecer como nuevo.',
    'changes.stability': 'Estabilidad',
    'changes.stabilityHelp': 'Cuánto del mapa comparten las dos versiones. Poca estabilidad puede querer decir que el delito se movió, o que el método es inestable con estos datos. Distinguirlo es trabajo del analista.',
    'changes.counts': '{0} se mantienen · {1} nuevos · {2} salieron · {3} del conjunto nuevo ya estaba',
    'changes.inputs': 'Qué cambió en la entrada',
    'changes.period': 'Periodo',
    'changes.incidents': 'Incidentes de entrada',
    'changes.method': 'Método',
    'changes.window': 'Ventana horaria',
    'changes.moved': 'Se movieron · {0}',
    'changes.movedHelp': 'Puntos que cruzaron al menos cinco lugares. Uno que subió le cambió el turno a alguien.',
    'changes.riskDelta': 'Riesgo {0} → {1}',
    'changes.new': 'Nuevos · {0}',
    'changes.newHelp': 'No estaban en la versión anterior: aquí va patrullaje que antes no iba.',
    'changes.dropped': 'Salieron · {0}',
    'changes.droppedHelp': 'Estaban en la versión anterior y ya no: estos lugares dejan de patrullarse.',
    'changes.point': '{0} incidentes · beat {1}',
    'changes.same': 'Los mismos puntos y en el mismo orden. Solo se movieron los números de atrás.',
    'changes.open': 'Qué cambió',
    'changes.close': 'Cerrar comparación',
    'admin.tabDeployment': 'Despliegue',
    'admin.tabAgency': 'Agencia',
    'admin.tabDevices': 'Teléfonos',
    'admin.tabAudit': 'Registro',
    'admin.tabTheme': 'Tema',
    'nav.insight': 'Desempeño',
    'insight.help': 'Lo planeado contra lo que pasó, turno por turno. Nada de aquí cambia el turno de hoy: es para aprender del anterior.',
    'insight.caveat': 'Estos números valen lo que valga la ejecución que los produjo. Miden cumplimiento, no efectividad: si el patrullaje bajó el delito es una afirmación causal y necesita una metodología acordada con el departamento.',
    'insight.simulated': 'Despliegue de demostración: estos turnos los ejecutó el simulador, así que los números describen al simulador y no a una policía.',
    'insight.shifts': 'Turnos',
    'insight.empty': 'Todavía no hay nada registrado. Un turno aparece aquí cuando se publica su plan.',
    'insight.shift': 'Turno',
    'insight.planned': 'Planeadas',
    'insight.released': 'Liberadas',
    'insight.completed': 'Completadas',
    'insight.missed': 'No atendidas',
    'insight.deferred': 'Diferidas',
    'insight.onTime': 'Puntualidad',
    'insight.presence': 'Permanencia',
    'insight.deviations': 'Desvíos',
    'insight.scenariosGenerated': '{0} {0|escenario evaluado|escenarios evaluados} para este turno antes de aprobar el plan',
    'insight.commandActivity': 'Pedidos de apoyo: {0} · Versiones del plan devueltas: {1} · Ajustes manuales: {2}',
    'insight.open': 'Repasar',
    'insight.review': 'Repaso del {0} · {1}',
    'insight.reviewHelp': 'No un total: quién hizo qué. Es lo que retroalimenta el método y el siguiente plan.',
    'insight.approvedBy': 'Aprobado por {0}',
    'insight.partial': 'escenario parcial, aceptado por la autoridad',
    'insight.resources': '{0} oficiales disponibles · {1} ausentes · {2} restringidos · {3} vehículos',
    'insight.deviationBreakdown': '{0} llegadas tarde · {1} permanencias cortas · {2} fuera de zona · {3} sin conexión · {4} sin iniciar el turno · {5} fuera de jurisdicción',
    'insight.reoptimizations': '{0} reoptimizaciones, {1} propusieron un cambio',
    'insight.reoptimizationCost': 'El motor tardó {0} ms por corrida',
    'insight.coverageRecovered': 'recuperó {0}{1} puntos de cobertura ponderada',
    'insight.units': 'Por unidad',
    'insight.unit': 'Unidad',
    'insight.assignments': 'Asignaciones',
    'insight.late': 'Retraso medio',
    'insight.stay': 'Permanencia / planeada',
    'insight.incidents': 'Incidentes durante el turno',
    'insight.incidentsHelp': 'Tiempo que una unidad estuvo fuera del plan atendiendo otra cosa.',
    'insight.open2': 'sigue abierto',
    'link.live': 'En vivo',
    'link.live.help': 'Conectado al servidor: lo que ve se actualiza solo.',
    'link.connecting': 'Reconectando',
    'link.connecting.help': 'Se cayó la conexión y se está reintentando. Lo que hay en pantalla puede estar atrasado.',
    'link.offline': 'Sin conexión',
    'link.offline.help': 'No hay conexión con el servidor. Lo que hay en pantalla es lo último que llegó y no se está actualizando.',
    'insight.rebuild': 'Rehacer la historia',
    'insight.rebuildHelp': 'El desempeño se arma con eventos, así que solo conoce lo ocurrido desde que se instaló el módulo. Esto reconstruye el último año con lo que guardaron TACTIX y SENTINEL. Borra y rehace, así que se puede repetir sin contar dos veces.',
    'insight.rebuilt': '{0} turnos reconstruidos a partir de {1} eventos.',
    'insight.emptyRebuild': 'Si esta instalación ya operaba antes, rehaga la historia.',
    'planner.tabShift': 'Turno',
    'planner.tabUnits': 'Unidades',
    'planner.tabScenarios': 'Escenarios',
    'planner.tabPlan': 'Plan',
    'trends.period': '{0} al {1} · {2} turnos',
    'trends.against': 'Comparado contra los {0} turnos anteriores, del mismo largo.',
    'trends.noPrevious': 'No hay nada antes de este periodo con qué comparar.',
    'trends.flat': 'sin cambio',
    'trends.export': 'Descargar hoja',
    'trends.compliance': 'Cumplimiento',
    'trends.onTime': 'Puntualidad',
    'trends.presence': 'Permanencia',
    'trends.work': 'Trabajo del periodo',
    'trends.planned': '{0} asignaciones planeadas',
    'trends.released': '{0} liberadas a una unidad',
    'trends.completed': '{0} completadas',
    'trends.incidents': '{0} incidentes · {1} min fuera del plan',
    'trends.kinds': 'Qué desvío domina',
    'trends.kindsHelp': 'El que se repite es sobre el que vale la pena actuar.',
    'trends.series': 'Turno por turno',
    'trends.seriesHelp': 'Cada barra es un turno: la altura es la puntualidad, y el ámbar avisa que algo quedó diferido. Pase el cursor para leerlo.',
    'trends.byShift': 'Por turno del día',
    'trends.byShiftHelp': '«Cuál turno se nos complica» es una pregunta de plantilla, no de personas.',
    'trends.byDivision': 'Por división',
    'trends.byDivisionHelp': 'La división a la que peor le va no siempre es la que más trabaja.',
    'trends.noDivision': 'sin división',
    'trends.shifts': 'Turnos',
    'cause.title': 'Qué se observa detrás',
    'cause.hypothesis': 'Es lo que muestran los datos, no una causa demostrada. Decide quien tenga el contexto.',
    'cause.late_release': 'se libera {0} min después de que ya era hora de estar ahí: eso es la anticipación de la liberación, no el plan',
    'cause.travel_underestimated': 'el plan le da {0} min menos de los que la unidad tarda en llegar: eso es la estimación de traslado',
    'cause.first_of_shift': 'casi siempre es la primera visita del turno: la unidad arranca ya atrasada',
    'cause.one_shift': 'se concentra en un turno del día: apunta a su dotación, no al punto',
    'cause.cases': '({0} de las llegadas tarde)',
    'cause.none': 'Nada se repite lo suficiente como para señalar una sola cosa.',

    'trends.spots': 'Puntos a los que llegamos tarde siempre',
    'trends.spotsHelp': 'Un punto con llegadas tarde repetidas casi siempre está mal puesto en el plan: demasiado lejos para la ventana que se le dio. Eso se arregla planificando, no exigiendo.',
    'trends.spot': 'Punto',
    'trends.visits': 'Visitas',
    'trends.lateVisits': 'Tarde',
    'trends.short': 'Permanencia corta',
    'trends.units': 'Unidades con más desvíos',
    'trends.unitsHelp': 'Esto no es una calificación de personas. Una unidad que llega tarde siempre suele estar diciendo que el traslado no cabe en el plan. Léalo como una pregunta, no como un veredicto.',
    'insight.tabTrends': 'Tendencias',
    'insight.tabShifts': 'Turnos',
    'nav.strategy': 'Estrategia',
    'strategy.help': 'Cómo patrulla esta agencia: el enfoque, el método analítico, la táctica y las metas contra las que se va a medir. Se aprueba una vez y se aplica a todos los turnos, en vez de que cada turno se planifique con las perillas que alguien recordó poner ese día.',
    'compareProfiles.open': 'Comparar',
    'compareProfiles.close': 'Cerrar comparación',
    'compareProfiles.title': 'Comparando {0} contra {1}',
    'compareProfiles.differentPeriods': 'Solo puede haber un perfil vigente a la vez, así que estos dos nunca cubrieron los mismos turnos. Esto no es un experimento: semanas distintas traen otro delito, otro clima y otra gente. Lea primero las metas y al final los números en crudo.',
    'compareProfiles.ownGoals': 'Qué prometió cada uno, y qué cumplió',
    'compareProfiles.ownGoalsHelp': 'Es la única comparación justa: cada perfil se puso su propia vara, así que cumplirla significa lo mismo para los dos. Alcanzar una meta modesta no es mejor que quedarse cerca de una ambiciosa.',
    'compareProfiles.noGoals': 'Ninguno de los dos se puso metas, así que no hay con qué medirlos.',
    'compareProfiles.goal': 'Meta (real / propuesta)',
    'compareProfiles.conditions': 'Con qué trabajaron',
    'compareProfiles.period': 'Periodo',
    'compareProfiles.shifts': 'Turnos planificados',
    'compareProfiles.officers': 'Oficiales disponibles por turno',
    'compareProfiles.absent': 'Oficiales ausentes por turno',
    'compareProfiles.incidents': 'Incidentes por turno',
    'compareProfiles.incidentMinutes': 'Minutos fuera del plan por turno',
    'compareProfiles.outcome': 'Qué resultó',
    'compareProfiles.planned': 'Asignaciones planeadas',
    'compareProfiles.released': 'Liberadas a una unidad',
    'compareProfiles.completed': 'Completadas',
    'compareProfiles.deferred': 'Diferidas',
    'compareProfiles.onTime': 'Puntualidad',
    'compareProfiles.presence': 'Permanencia',
    'compareProfiles.late': 'Llegadas tarde',
    'compareProfiles.shortPresence': 'Permanencias cortas',
    'compareProfiles.footnote': 'Un perfil con pocos turnos detrás dice menos que uno con muchos: el número de turnos es el peso de todo lo de arriba.',
    'strategy.active': 'Perfil vigente',
    'strategy.none': 'Esta agencia todavía no ha aprobado un perfil. Los turnos se planifican con el preset de fábrica.',
    'strategy.method': 'Método: {0} · ventana {1} · {2} hotspots · {3} días de histórico',
    'strategy.tactic': 'Táctica: {0} · ajustes {1}',
    'strategy.goals': 'Metas: {0} de puntualidad · {1} de permanencia · hasta {2} diferidas por turno',
    'strategy.signed': 'Propuesto por {0} · validado por {1} · aprobado por {2} el {3}',
    'strategy.propose': 'Proponer un perfil',
    'strategy.editDraft': 'Editar borrador',
    'strategy.proposeHelp': 'El analista lo redacta, el planificador dice si se puede operar con la gente que hay, y el mando aprueba el enfoque. Tres firmas, tres personas.',
    'strategy.name': 'Nombre',
    'strategy.division': 'Alcance',
    'strategy.divisionAll': 'Toda la agencia',
    'strategy.divisionHelp': 'Un perfil de división va encima del de la agencia y solo puede cambiar la permanencia —como factor sobre la que ya trae cada hotspot—, el periodo de revisita, la tolerancia de retraso, el traslado máximo y el tope de postergación. No lleva método analítico: cuántos hotspots se calculan y con cuántos incidentes fija la escala contra la que se normaliza el riesgo, que es la misma con la que se compara una división con otra.',
    'strategy.framework': 'Enfoque institucional',
    'strategy.rationale': 'Por qué',
    'strategy.rationaleHelp': 'Lo que alguien va a leer dentro de un año para entender por qué la agencia patrulla así.',
    'strategy.analytics': 'Método analítico',
    'strategy.analyticsHelp': 'Lo que ATLAS usa por omisión para calcular los hotspots con los que trabaja este perfil.',
    'strategy.timeWindow': 'Ventana horaria',
    'strategy.topHotspots': 'Hotspots por conjunto',
    'strategy.minIncidents': 'Mínimo de incidentes',
    'strategy.trainingDays': 'Días de histórico',
    'strategy.tacticTitle': 'Táctica',
    'strategy.tacticHelp': 'Lo que TACTIX usa por omisión. El planificador puede probar otra cosa en un escenario —para eso existen— pero no por descuido.',
    'strategy.preset': 'Preset del optimizador',
    'strategy.overrides': 'Ajustes (JSON)',
    'strategy.targets': 'Metas',
    'strategy.targetsHelp': 'Lo que la agencia espera. Sin ellas, evaluar el perfil es mirar números sin vara: 68 % de puntualidad no es bueno ni malo hasta que alguien dijo a qué aspiraba.',
    'strategy.onTimeRate': 'Puntualidad (0–1)',
    'strategy.presenceRate': 'Permanencia (0–1)',
    'strategy.maxDeferred': 'Diferidas por turno',
    'strategy.proposeAction': 'Proponer',
    'strategy.save': 'Guardar borrador',
    'strategy.cancel': 'Cancelar',
    'strategy.all': 'Perfiles · {0}',
    'strategy.noneYet': 'Todavía no se ha propuesto ninguno.',
    'strategy.compareHint': 'Marque dos para compararlos.',
    'strategy.proposedBy': 'Propuesto por {0} el {1}',
    'strategy.returned': 'Devuelto: {0}',
    'strategy.approvedNote': 'Al aprobarlo: {0}',
    'strategy.retiredNote': 'Al retirarlo: {0}',
    'strategy.validatedNote': 'Al validarlo: {0}',
    'strategy.scopeAgency': 'Toda la agencia',
    'strategy.scopeDivision': 'División {0}, encima del perfil de la agencia',
    'strategy.note': 'Nota',
    'strategy.edit': 'Editar',
    'strategy.validate': 'Validar',
    'strategy.return': 'Devolver',
    'strategy.approve': 'Aprobar',
    'strategy.retire': 'Retirar',
    'strategyStatus.Draft': 'propuesto · falta validarlo',
    'strategyStatus.Returned': 'devuelto · lo corrige el analista',
    'strategyStatus.Validated': 'validado · falta aprobarlo',
    'strategyStatus.Active': 'vigente',
    'strategyStatus.Retired': 'retirado',
    'trends.byProfile': 'Por perfil de estrategia',
    'trends.byProfileHelp': 'Cómo le fue a cada perfil. Es lo que cierra el círculo: el mando aprueba un perfil, INSIGHT lo evalúa, y esa evaluación alimenta la versión siguiente.',
    'trends.noProfile': 'sin perfil',
    'denial.event.id_missing': 'El evento llegó sin identificador, así que no se puede reconocer si se reenvía.',
    'denial.event.in_future': 'El evento está fechado en el futuro: revise el reloj del teléfono.',
    'denial.event.needs_assignment': 'Este evento tiene que decir a qué asignación pertenece.',
    'denial.event.needs_position': 'Este evento tiene que traer una posición.',
    'denial.event.bad_coordinates': 'Las coordenadas están fuera de rango.',
    'denial.event.bad_accuracy': 'La precisión de la posición no sirve.',
    'denial.assignment.missing': 'Esa asignación no existe.',
  'denial.token.incomplete': 'El ingreso está incompleto. Vuelva a ingresar.',
  'denial.device.unregistered': 'Ese teléfono no está registrado en la agencia.',
  'denial.device.revoked': 'Ese teléfono fue revocado.',
  'denial.device.other_agency': 'Ese teléfono pertenece a otra agencia.',
  'denial.agency.unknown': 'Agencia desconocida: {agency}.',
  'denial.agency.other_host': 'Esta cuenta es de otra agencia que la de esta dirección.',
  'signin.goToOwnHost': 'Ir a {host}',
  'denial.duty.off_duty': 'Ese oficial no integra una unidad de un turno en curso.',
  'denial.duty.shift_closed': 'La unidad {callSign} ya cerró el turno.',
  'denial.duty.absent': 'RR. HH. publicó a ese oficial como ausente para este turno.',
  'denial.batch.too_large': 'Demasiados registros a la vez: el servidor admite hasta {max}.',
  'denial.event.too_old': 'El registro es de hace más de {hours} horas.',
  'denial.unit.unknown': 'Esa unidad ya no existe.',
  'denial.assignment.not_ours': 'Esa asignación es de otra unidad.',
  'denial.vehicle.mismatch': 'El vehículo {confirmed} no es el de la unidad {callSign} ({expected}).',
  'denial.state.invalid': 'Ese paso no se admite en este momento.',
  'denial.request.invalid': 'El servidor rechazó el pedido.',
  'denial.not_found': 'Ya no existe en el servidor.',
  'denial.app.session_expired': 'La sesión venció. Vuelva a ingresar.',
  'denial.app.forbidden': 'Su rol no permite esto.',
  'denial.app.unreachable': 'Sin conexión con el servidor.',
  'denial.scenario.no_availability': 'No hay disponibilidad publicada para este turno: pídasela a RH (oficiales) y a Flota (vehículos).',
  'denial.scenario.no_units': 'No hay unidades conformadas para este turno: ármelas en Unidades antes de generar.',
  'denial.ingest.not_configured': 'Al servidor le falta la taxonomía NIBRS o la política de niveles, así que no puede traer reportes. Avise a quien administra la instalación.',
  'denial.app.server_error': 'El servidor no pudo terminar esto. Vuelva a intentarlo y, si se repite, dé la referencia a soporte.',
}

/**
 * Los dos diccionarios.
 *
 * Se exportan para que las pruebas los recorran enteros. Las claves que faltaban en inglés se
 * encontraron de a una, mirando pantallas; una prueba que compare los dos juegos completos las
 * encuentra todas el día que alguien agrega una.
 */
export const dictionaries: Record<Lang, Record<Key, string>> = { en, es }

/** El idioma del navegador, salvo que el usuario haya elegido otro. */
export function initialLang(): Lang {
  const saved = localStorage.getItem('lang')
  if (saved === 'en' || saved === 'es') return saved
  return navigator.language.toLowerCase().startsWith('es') ? 'es' : 'en'
}

/**
 * Traduce. Acepta datos por posición ({0}) o por nombre ({callSign}), porque los rechazos del
 * backend traen sus argumentos con nombre.
 */
export function translate(lang: Lang, key: string, args?: unknown[] | Record<string, string> | null): string {
  const text = dictionaries[lang][key as Key] ?? dictionaries.en[key as Key]
  if (text === undefined) return key
  if (!args) return text
  const valorDe = (name: string): unknown => (Array.isArray(args) ? args[Number(name)] : args[name])
  // Plurales: «{0|oficial|oficiales}» elige por el valor de {0}. Sin esto la pantalla decía «1
  // unidades» y «1 oficiales disponibles» (hallazgo H-38 de la prueba integral). El número va en su
  // propio {0}; esto elige solo la palabra, en español y en inglés el 1 es singular y todo lo demás
  // —el cero incluido— plural.
  const conPlurales = text.replace(/\{(\w+)\|([^|}]*)\|([^}]*)\}/g, (whole, name: string, uno: string, varios: string) => {
    const value = Number(valorDe(name))
    return Number.isNaN(value) ? whole : value === 1 ? uno : varios
  })
  return conPlurales.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = valorDe(name)
    // Solo se interpolan primitivas: un objeto se vería como «[object Object]» en la pantalla,
    // que es peor que dejar el hueco a la vista.
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : whole
  })
}

/**
 * La explicación de una alerta en el idioma del operador. Con un tipo que esta versión no conozca,
 * se muestra la frase que armó el backend: nadie se queda sin explicación.
 */
export function alertText(lang: Lang, alert: { kind: string; message: string; args?: Record<string, string> | null }): string {
  // Una sola visita no es «1 visitas», ni tiene «primera».
  const key = `alert.${alert.kind}${(alert.kind === 'ShiftNotStarted' || alert.kind === 'UnitUnavailable') && alert.args?.visits === '1' ? '.one' : ''}`
  const known = dictionaries[lang][key as Key] ?? dictionaries.en[key as Key]
  if (known === undefined || !alert.args) return alert.message
  // La distancia en las unidades de la agencia (H-64): el servidor la manda en metros o kilómetros.
  const args: Record<string, string> = { ...alert.args }
  if (args.meters) args.distance = fmtDistance(Number(args.meters), lang)
  else if (args.km) args.distance = fmtDistance(Number(args.km) * 1000, lang)
  return translate(lang, key, args)
}

/**
 * El detalle de una regla de conformación en el idioma de quien planifica.
 *
 * El servidor manda el código y los datos por separado; la frase se arma aquí. Si esta versión no
 * conoce el código, se muestra la que armó el servidor: nadie se queda sin saber qué pasó.
 *
 * El motivo de una ausencia o una restricción lo escribió una persona, así que no se traduce: se
 * agrega entre paréntesis tal cual vino.
 */
export function ruleText(
  lang: Lang,
  violation: { ruleCode: string; message: string; args?: Record<string, string> | null },
): string {
  const args = violation.args
  const key = `ruleDetail.${violation.ruleCode}${args?.case ? `.${args.case}` : ''}`
  const known = dictionaries[lang][key as Key] ?? dictionaries.en[key as Key]
  if (known === undefined || !args) return violation.message
  // Los datos llegan como los guarda el servidor: el turno como «2026-09-21/INTEGRAL» y el estado
  // del vehículo como «Maintenance». En la frase van legibles (hallazgo H-09 de la prueba integral).
  const legibles: Record<string, string> = { ...args }
  const turno = /^(\d{4}-\d{2}-\d{2})\/(.+)$/.exec(args.shift ?? '')
  if (turno?.[1] && turno[2]) legibles.shift = translate(lang, 'rule.shift', [turno[2], dateText(lang, turno[1])])
  if (args.status && dictionaries[lang][`vehicle.${args.status}` as Key] !== undefined)
    legibles.status = translate(lang, `vehicle.${args.status}`)
  const frase = translate(lang, key, legibles)
  // El motivo llega como código del sistema de la agencia («LIGHT_DUTY»): los conocidos se dicen en
  // palabras; uno que no se conoce se muestra tal cual antes que callarlo.
  const motivo = args.reason && (dictionaries[lang][`reason.${args.reason}` as Key] ?? args.reason)
  return motivo ? `${frase} (${motivo})` : frase
}

/**
 * Una fecha sin hora —«2026-09-19»— en el idioma de quien mira.
 *
 * No se puede pasar por `new Date(iso)` a secas: una cadena de solo fecha se interpreta en UTC,
 * y al formatearla en la zona local se corre un día hacia atrás en todo el hemisferio occidental.
 * El resumen de un vehículo llegó a decir «del 18 al 23» sobre turnos del 19 al 24.
 *
 * Con la hora pegada, la misma cadena se interpreta en local y la fecha es la que dice.
 */
export function dateText(lang: Lang, isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString(lang === 'es' ? 'es' : 'en')
}

/** El motivo de un rechazo en el idioma del usuario; si el código es nuevo, el texto del servidor. */
/** Códigos que solo dicen la categoría; el porqué viene en el texto del servidor. */
const GENERIC_DENIALS = new Set(['state.invalid', 'request.invalid'])

export function denialText(
  lang: Lang,
  denial: { code: string; reason: string; args?: Record<string, string> | null; reference?: string },
): string {
  const key = `denial.${denial.code}`
  const known = dictionaries[lang][key as Key] ?? dictionaries.en[key as Key]
  // Los códigos genéricos no dicen nada por sí solos: «ese paso no se admite» escondía «el plan
  // cambió desde la corrida, reoptimice de nuevo» o «la aprueba otra persona» (H-40). Si el
  // servidor explicó, se muestra su explicación; hoy la escribe en español.
  const generico = GENERIC_DENIALS.has(denial.code) && !denial.args && denial.reason
  const texto = known === undefined
    ? denial.reason || translate(lang, 'denial.request.invalid')
    : generico
      ? (lang === 'es' ? denial.reason : `${translate(lang, key)} ${denial.reason}`)
      : translate(lang, key, denial.args ?? null)
  // Los ocho primeros caracteres: se dictan por radio sin equivocarse, y en el registro se
  // encuentran igual buscando el TraceId que empieza así.
  return denial.reference ? `${texto} (${translate(lang, 'denial.reference', [denial.reference.slice(0, 8)])})` : texto
}

/**
 * Las notas que escribe el propio sistema al reemplazar versiones del plan, en el idioma de quien lee.
 *
 * Se guardan en español —«reemplazada por la reoptimización 3»— y en la pantalla en inglés salían
 * así (hallazgo H-20 de la prueba integral). Lo que escribió una persona no se toca: solo se
 * reconocen estas formas conocidas.
 */
const NOTAS_DEL_SISTEMA: [RegExp, string][] = [
  [/^reemplazada por el ajuste manual (\d+)$/, 'note.byManual'],
  [/^reemplazada por la reoptimización (\d+)$/, 'note.byReoptimization'],
  [/^reemplazada por la versión (\d+)$/, 'note.byVersion'],
  [/^reemplazada por una propuesta posterior$/, 'note.byLaterProposal'],
  [/^reemplazada por una reoptimización posterior$/, 'note.byLaterReoptimization'],
]

export function systemNote(lang: Lang, text: string): string {
  for (const [forma, clave] of NOTAS_DEL_SISTEMA) {
    const m = forma.exec(text)
    if (m) return translate(lang, clave, m.slice(1))
  }
  return text
}
